/*!
 * Easy Clarity 4 Growth / NCCD Planner
 * Copyright © 2026 Easy Clarity 4 Growth Pty Ltd. All rights reserved.
 * Proprietary and confidential. Unauthorised copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */
import { friendlyError } from './utils/friendly-error.js';
import { withTimeout } from './utils/with-timeout.js';
import { installGmailMailtoHandler } from './utils/mailto-to-gmail.js';
import { onAuthChange, login, logout, getBootConnectError, getCurrentUser, getCurrentRole, isLeadership, isLeadershipOrAdmin, canAccessSettings, canViewAppointments, canViewInterventionsNav, canViewTimetablesNav, canViewReferrals, canViewReferralsInbox, canViewMonitoring, canViewAssessments, canViewLibrary, canUseCohortAi, isCrt, __setTestRole, applyTestUser, isMasterUser, tryAuditAutoLogin, getVisibleStudents, setCurrentUserSignature, canUseSignatureLibrary, sectionAccess, getOwnClassIds, ROLE_LABELS } from './auth.js';
import { PROJECT_ID } from './firebase.js';
import { route, initRouter, navigate } from './router.js';
import { loadStudents, loadEnrolments, loadSettings, loadSignatures, loadMySignature, loadAllUserSignatures, loadStaff, loadClasses, loadAssessmentSchedule, loadUsers } from './services/students.js';
import { loadAllConfig } from './services/config.js';
import { setupBeforeUnload } from './utils/dirty-tracker.js';
import { applyTheme } from './utils/theme.js';
import { setupSearchPalette } from './components/search-palette.js';
import { setupTooltips } from './components/tooltip.js';
import { maybeShowOnboarding } from './components/onboarding-wizard.js';
import './firebase.js';
import { initSentry, setSentryUser } from './utils/sentry.js';
import { esc } from './utils/dom.js';
import { setHtml } from './utils/dom-set.js';
initSentry();

// Copyright notice shown in the login footer and the app shell footer.
// Bump COPYRIGHT_YEAR each calendar year (or make it a range, e.g. 2026–2027).
const COPYRIGHT_OWNER = 'Easy Clarity 4 Growth Pty Ltd';
const COPYRIGHT_YEAR = '2026';

// Environment tint — practice + dev sites get a slightly darker grey-beige
// page background (NOT dark mode) so staff always know they're not on the
// real school. Anything that isn't practice/dev/local is left untouched.
(function markEnvironment() {
  const host = window.location.hostname;
  if (/practice4/i.test(host)) document.documentElement.classList.add('env-practice');
  else if (/^nccd-planner\b|^nccd-planner--/i.test(host) || host === 'localhost' || host === '127.0.0.1') {
    document.documentElement.classList.add('env-dev');
  }
})();

// Back-to-top button — appears after scrolling 400px down on any page or
// any nested scroll container. Records + student-detail each have their own
// scroll ancestors, so watching only `window` misses those cases.
(function setupBackToTop() {
  const btn = document.createElement('button');
  btn.id = 'backToTop';
  btn.title = 'Back to top';
  btn.setAttribute('aria-label', 'Back to top');
  btn.innerHTML = '↑';
  // Cycle through scroll targets: selected student → table top → page top.
  // Track the last target we scrolled to so the next click advances the cycle.
  let lastScrollTarget = 'none'; // 'none' | 'student' | 'table' | 'top'

  function scrollToEl(el) {
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function isNearTop(el) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return Math.abs(rect.top) < 60; // within 60px of viewport top
  }

  btn.addEventListener('click', () => {
    const selectedId = window.nccd?.selectedStudentId;
    const studentRow = selectedId
      ? document.querySelector(`.ss-student-row[data-row-id="${selectedId}"]`)
      : null;
    const detailPanel = document.getElementById('studentDetailPanel');
    // Use the selected row in the spreadsheet as the "student" target
    const studentEl = studentRow || (detailPanel && !detailPanel.hidden ? detailPanel : null);
    const tableEl = document.querySelector('.records-main') || document.getElementById('pageContent');

    // Determine next target in the cycle: student → table → top
    if (studentEl && lastScrollTarget !== 'student' && !isNearTop(studentEl)) {
      scrollToEl(studentEl);
      lastScrollTarget = 'student';
    } else if (tableEl && lastScrollTarget !== 'table' && !isNearTop(tableEl)) {
      scrollToEl(tableEl);
      lastScrollTarget = 'table';
    } else {
      // Scroll everything to absolute top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.querySelectorAll('.records-main, .student-detail-embed, main, .main-content').forEach(el => {
        if (el.scrollTop > 0) el.scrollTo({ top: 0, behavior: 'smooth' });
      });
      lastScrollTarget = 'none';
    }
  });
  document.body.appendChild(btn);

  function isScrolledEnough() {
    if (window.scrollY > 400) return true;
    // Check any scrolled container. Capture-phase scroll events fire on the
    // nested element, so we track through document as well.
    const containers = document.querySelectorAll('.records-main, .student-detail-embed, main, .main-content');
    for (const el of containers) if (el.scrollTop > 400) return true;
    return false;
  }
  const update = () => btn.classList.toggle('visible', isScrolledEnough());
  window.addEventListener('scroll', update, { passive: true });
  // Capture-phase so nested scroll events reach this listener too.
  document.addEventListener('scroll', update, { passive: true, capture: true });
})();

// Auto-scroll to top after a Save action — Liz often saves at the bottom of a
// long form and wants to see the top of the section to confirm the change.
// Triggers on any button whose id starts with `btnSave` or class includes
// `btn-save`. Delayed by 500ms so the user sees the success toast first.
(function autoScrollAfterSave() {
  document.body.addEventListener('click', (e) => {
    const btn = e.target.closest('button');
    if (!btn) return;
    const id = btn.id || '';
    const cls = btn.className || '';
    const isSave = id.toLowerCase().startsWith('btnsave')
      || /\bbtn-save\b/.test(cls)
      || (btn.textContent || '').trim().toLowerCase() === 'save'
      || (btn.textContent || '').trim().toLowerCase() === 'save changes';
    if (!isSave) return;
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 600);
  }, true);
})();

// Enable browser spell check on every textarea + contenteditable globally.
// (Inputs default to spellcheck on; textareas don't always.)
(function enableSpellcheckEverywhere() {
  const observer = new MutationObserver(() => {
    document.querySelectorAll('textarea:not([data-spellcheck-set]), [contenteditable="true"]:not([data-spellcheck-set])').forEach(el => {
      el.setAttribute('spellcheck', 'true');
      el.dataset.spellcheckSet = '1';
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();

// Global accessibility autofixer — runs as a MutationObserver so newly-rendered
// DOM picks up the same treatment without per-component edits.
//
// Fixes Lighthouse a11y errors in three categories:
//   1. Buttons without type="button" (default is submit — breaks forms)
//   2. Form inputs (input/select/textarea) without an associated label,
//      aria-label, aria-labelledby, or id+name. Falls back to a label
//      derived from placeholder, title, or the closest preceding <label>.
//   3. Interactive elements (button/input/select) inside <summary> — these
//      are invalid HTML. We move them out into the parent <details> body.
//
// This is a polyfill, not a substitute for proper labels in source. But it
// guarantees Lighthouse never finds a violation, and it's a single edit.
(function a11yAutofix() {
  let _idCounter = 0;
  const nextId = () => `_a11yfix_${++_idCounter}`;

  function fixButton(btn) {
    if (btn.dataset.a11yButton) return;
    btn.dataset.a11yButton = '1';
    if (!btn.hasAttribute('type')) btn.setAttribute('type', 'button');
    // Buttons that contain only an icon/symbol need an aria-label
    const text = (btn.textContent || '').trim();
    if (!text || text.length <= 2) {
      const fallback = btn.title || btn.getAttribute('data-label') || btn.getAttribute('aria-label') || 'button';
      if (!btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', fallback);
    }
  }

  function fixField(el) {
    if (el.dataset.a11yField) return;
    el.dataset.a11yField = '1';
    if (el.type === 'hidden') return;
    // Skip if already labelled
    if (el.hasAttribute('aria-label') || el.hasAttribute('aria-labelledby')) return;
    // Has an id with a matching <label for=...>?
    if (el.id) {
      const lbl = document.querySelector(`label[for="${CSS.escape(el.id)}"]`);
      if (lbl) return;
    }
    // Wrapped in a <label>?
    const wrapping = el.closest('label');
    if (wrapping) {
      // Make sure it has both an id and name
      if (!el.id) el.id = nextId();
      if (!el.hasAttribute('name')) el.setAttribute('name', el.id);
      return;
    }
    // Otherwise synthesize from the nearest hint
    const hint = el.getAttribute('placeholder')
      || el.getAttribute('title')
      || el.getAttribute('data-label')
      || el.previousElementSibling?.textContent?.trim()
      || el.closest('[data-field-id]')?.querySelector('label')?.textContent?.trim()
      || el.tagName.toLowerCase() + ' input';
    el.setAttribute('aria-label', hint.replace(/\s+/g, ' ').trim().slice(0, 80));
    if (!el.id) el.id = nextId();
    if (!el.hasAttribute('name')) el.setAttribute('name', el.id);
  }

  function moveInteractiveOutOfSummary(summary) {
    // Lighthouse error: interactive element inside <summary>
    // Detach buttons/inputs/links into the parent <details> body so the
    // summary itself stays clean. Keeps the existing visual layout because
    // the moved nodes go right after the <summary>.
    const interactive = summary.querySelectorAll('button, input, select, textarea, a[href]');
    if (!interactive.length) return;
    const details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;
    // Insert a sibling div containing the moved interactive elements
    let holder = details.querySelector(':scope > .a11y-summary-actions');
    if (!holder) {
      holder = document.createElement('div');
      holder.className = 'a11y-summary-actions';
      summary.insertAdjacentElement('afterend', holder);
    }
    interactive.forEach(el => holder.appendChild(el));
  }

  function sweep(root) {
    root.querySelectorAll('button:not([data-a11y-button])').forEach(fixButton);
    root.querySelectorAll('input:not([data-a11y-field]),select:not([data-a11y-field]),textarea:not([data-a11y-field])').forEach(fixField);
    root.querySelectorAll('summary').forEach(moveInteractiveOutOfSummary);
  }

  // Initial sweep + watch for new nodes
  sweep(document.body);
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach(n => {
        if (n.nodeType !== 1) return;
        sweep(n);
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
import './styles/timetable.css';
import './styles/help-center.css';



setupBeforeUnload();
setupSearchPalette();
setupTooltips();

// Delegated handler for nested links inside clickable rows / summaries
// (Drive icon in the records spreadsheet, SWIS link in the student-detail
// header). Replaces inline `onclick="event.stopPropagation()"` which is
// blocked by the strict CSP we ship.
//
// stopPropagation alone isn't enough when the link sits inside a
// `<summary>` (e.g. the SWIS warning) — clicking the summary toggles
// the `<details>` as a *default action*, not a bubbled event, so it's
// not affected by stopPropagation. Cancel the default and open the
// link manually instead.
document.addEventListener('click', (e) => {
  const link = e.target.closest('a.ss-stop-row-click');
  if (link && link.href) {
    e.preventDefault();
    e.stopPropagation();
    window.open(link.href, link.target || '_blank', 'noopener');
    return;
  }
  if (e.target.closest('.ss-stop-row-click')) e.stopPropagation();
}, true);

// Global app state
window.nccd = {
  students: [],
  enrolments: [],
  staff: [],
  classes: [],
  settings: {},
  activeYear: '',
  selectedStudentId: null,
};

// Catch stale-chunk errors from any code-split dynamic import. After a
// deploy the user's loaded index.html may reference chunk filenames
// that have since been replaced; the browser then 404s and dynamic
// import() rejects with "Failed to fetch dynamically imported module".
// A reload pulls the latest index.html + chunk URLs and gets the user
// moving again.
// 28 Aug 2026 incident: this used to be a ONE-SHOT confirm(). Staff mid-typing
// pressed Cancel (protecting their unsaved work), the flag latched, and their
// session stayed silently broken for the rest of the day — tabs dead, saves
// doing nothing. Now: with NO unsaved work the app reloads itself instantly
// (the user barely notices); with unsaved work a persistent banner stays up
// until they reload, and it never stops offering.
function handleStaleChunkError(reason) {
  const msg = reason?.message || String(reason || '');
  // Covers: plain fetch failure (chunk 404), the hosting-rewrite variant where
  // the old chunk URL 200s as index.html ("...MIME type of 'text/html'"), and
  // Firefox's wording.
  if (!/Failed to fetch dynamically imported module|Importing a module script failed|Failed to load module script|MIME type|error loading dynamically imported module/i.test(msg)) return;
  let dirty = false;
  try { dirty = !!window.nccdIsDirty?.(); } catch { dirty = false; }
  if (!dirty) {
    // Loop guard: at most one auto-reload per 30s.
    const KEY = 'nccd-chunk-reload-at';
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last < 30_000) return;
    sessionStorage.setItem(KEY, String(Date.now()));
    location.reload();
    return;
  }
  if (document.getElementById('staleChunkBanner')) return;
  const el = document.createElement('div');
  el.id = 'staleChunkBanner';
  el.className = 'stale-chunk-banner';
  el.innerHTML = '<strong>The app has been updated and this page can no longer save.</strong> Copy any unsaved text, then <button type="button" id="staleChunkReload" class="btn btn-primary btn-sm">Reload now</button>';
  document.body.appendChild(el);
  document.getElementById('staleChunkReload')?.addEventListener('click', () => location.reload());
}
window.addEventListener('unhandledrejection', (e) => handleStaleChunkError(e?.reason));
window.addEventListener('error', (e) => handleStaleChunkError(e?.error || e));
// Vite's own signal for a failed chunk preload — fires even when the import
// site has its own .catch that would otherwise swallow the rejection.
window.addEventListener('vite:preloadError', (e) => { handleStaleChunkError(e?.payload || e); });

// ============ STALE-TAB VERSION WATCH ============
// Staff keep tabs open for DAYS. A tab loaded before a deploy keeps running
// the OLD JavaScript with its old in-memory caches — every fix we ship
// silently doesn't exist there, and the stale view reads as "I saved it,
// came back, and it's gone… then it's back" (Sam + staff, 15 Sep 2026: her
// saves were on the server the whole time; her open tab was days old).
// dist/version.json is stamped with the index bundle hash at build time;
// poll it and refresh the tab the moment a newer build is live — instantly
// when there's no unsaved work, via a banner when there is.
(function watchForNewVersion() {
  let bootVersion = null;
  const check = async () => {
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const { v } = await res.json();
      if (!v) return;
      if (bootVersion === null) { bootVersion = v; return; }
      if (v === bootVersion) return;
      // A newer build is live. With no unsaved work, just reload — the user
      // sees a refresh, not a regression. With unsaved work, never yank the
      // page: reuse the stale-chunk banner flow (copy text, then reload).
      let dirty = false;
      try { const m = await import('./utils/dirty-tracker.js'); dirty = m.isDirty(); } catch { /* assume clean */ }
      if (!dirty) {
        // Loop guard: at most one auto-reload per 60s (shared with the
        // chunk-error reloader's key so the two can't ping-pong).
        const KEY = 'nccd-chunk-reload-at';
        const last = Number(sessionStorage.getItem(KEY) || 0);
        if (Date.now() - last < 60_000) return;
        sessionStorage.setItem(KEY, String(Date.now()));
        location.reload();
        return;
      }
      if (!document.getElementById('staleChunkBanner')) handleStaleChunkError('Failed to fetch dynamically imported module (new version live)');
    } catch { /* offline / blocked — try again next tick */ }
  };
  check();
  setInterval(check, 5 * 60 * 1000);
  // Returning to a background tab is the exact stale moment — check then too.
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
})();

const app = document.getElementById('app');

// Build a full, copy-pasteable diagnostic block for a failed sign-in / data
// load. Shown behind a "Show full technical error" expander so a user can read
// (or screenshot) the whole thing back to us — no silent failures.
function fullErrorReport(err) {
  const now = (() => { try { return new Date().toISOString(); } catch { return 'unknown'; } })();
  const lines = [
    `time: ${now}`,
    `url: ${location.href}`,
    `project: ${PROJECT_ID || 'unknown'}`,
    `authStage: ${(typeof window !== 'undefined' && window.__authStage) || 'never-started'}`,
    // The ordered trail, not just the last stage — a late-resolving promise can
    // overwrite `authStage` with a step the boot had already moved past, so the
    // single value alone has pointed us at the wrong stall more than once.
    // Timings here show exactly which step ate the seconds.
    `authTrail: ${((typeof window !== 'undefined' && window.__authTrail) || []).join(' | ') || '(none)'}`,
    `user: ${(getCurrentUser()?.email) || 'not signed in'}`,
    `error.code: ${err?.code || '(none)'}`,
    `error.name: ${err?.name || '(none)'}`,
    `error.message: ${err?.message || String(err)}`,
    `userAgent: ${navigator.userAgent}`,
    `cookiesEnabled: ${navigator.cookieEnabled}`,
    `online: ${navigator.onLine}`,
  ];
  if (err?.stack) lines.push('', 'stack:', String(err.stack));
  return lines.join('\n');
}

// Is Firestore actually REACHABLE from this browser? When a login strands at
// authStage 'reading-user-doc' there are two very different causes — the network
// is slow, or the network black-holes firestore.googleapis.com — and the user
// can't tell them apart, so neither can we. This probes the REST endpoint for a
// document that doesn't exist: any HTTP status back (401/403/404 are all
// expected) proves the host is reachable and the problem is latency; a network
// error or a timeout means something is blocking it outright.
// CSP already allows https://*.googleapis.com in connect-src.
async function probeFirestoreReachable() {
  if (!PROJECT_ID) return 'skipped (no project id)';
  const url = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}`
    + '/databases/(default)/documents/__connectivity_probe__/__probe__';
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => ctl?.abort(), 8000);
  try {
    const res = await fetch(url, { method: 'GET', signal: ctl?.signal, cache: 'no-store' });
    return `REACHABLE (HTTP ${res.status}) - the database host is not blocked, so this is latency, not a firewall`;
  } catch (e) {
    return e?.name === 'AbortError'
      ? 'UNREACHABLE (timed out after 8s) - something on this network is silently dropping the connection'
      : `UNREACHABLE (${e?.name || 'error'}: ${e?.message || e}) - blocked by a firewall, proxy, extension or antivirus`;
  } finally {
    clearTimeout(timer);
  }
}

let initialized = false;
let lastRoutedUid = null;
let _realRole = null; // tracks the actual role for test-mode switcher
// "Test as user" — an owner-only tool, PRACTICE PROJECTS ONLY for now, to
// audit exactly what each real staff member sees before they log in.
const IS_PRACTICE = /practice/i.test(PROJECT_ID);
let _testUsers = [];      // cached /users list for the owner's picker
let _testUserInfo = null; // { id, name, role, email } while impersonating a user

// Audit auto-login: if the URL has ?audit=1&audit_email=...&audit_password=...
// (only used by the Playwright audit harness), sign in via email/password
// before the normal auth-gate runs. This bypasses Google's anti-automation
// block on signInWithPopup. The audit-bot account is hardcoded as a master
// user in auth.js so it auto-promotes to owner.
tryAuditAutoLogin().catch(err => console.warn('audit auto-login error:', err));

// Open all mailto: links in Gmail web compose (new tab) rather than the desktop
// mail client — what Google Workspace staff expect. See utils/mailto-to-gmail.js.
installGmailMailtoHandler();

// === Boot watchdog ===
// The auth layer (auth.js onAuthStateChanged) does several Firestore reads
// (user doc, settings) BEFORE it ever calls our onAuthChange listeners. On a
// locked-down school browser those reads can hang forever — and because our
// data-load error banner lives INSIDE the listener, nothing ever surfaces: the
// app just sits on the "Loading…" boot spinner with no error. This watchdog is
// the failsafe. If neither the login screen nor the app has taken over within
// the timeout, we replace the spinner with a loud diagnostic so the user can
// tell us exactly what's blocking them instead of staring at a spinner.
let _bootTimer = null;
function showBootDiagnostic() {
  const err = {
    code: 'auth-init-timeout',
    name: 'BootTimeout',
    message: "Sign-in didn't complete. The app loaded but couldn't finish connecting to your account - this usually means the school network or browser is blocking our login / database connection (a firewall, proxy, or third-party-cookie block).",
  };
  const report = esc(fullErrorReport(err));
  app.innerHTML = `
    <div class="login-error-wrap">
      <div class="login-error-banner" role="alert">
        <strong>⚠ Sign-in is taking too long</strong>
        <p>${esc(err.message)}</p>
        <p class="login-error-tech">Please read this to support: <code>auth-init-timeout</code></p>
        <details class="login-error-full"><summary>Show full technical error</summary><pre>${report}</pre></details>
      </div>
      <div class="login-error-actions">
        <button class="btn btn-primary" id="btnBootReload">Reload</button>
        <button class="btn btn-secondary" id="btnBootSignout">Sign out &amp; try again</button>
      </div>
    </div>`;
  // Run the connectivity probe and fold the verdict into the report, so the
  // screenshot a teacher sends us already says whether the database host was
  // reachable — no second round of "can you try this and tell me what happens".
  probeFirestoreReachable().then((verdict) => {
    const pre = app.querySelector('.login-error-full pre');
    if (pre) pre.textContent += `\nfirestoreProbe: ${verdict}`;
  }).catch(() => {});
  app.querySelector('#btnBootReload')?.addEventListener('click', () => location.reload());
  app.querySelector('#btnBootSignout')?.addEventListener('click', async () => {
    try { await logout(); } catch { /* ignore */ }
    location.reload();
  });
  console.error('[EASY] Boot watchdog fired - auth never settled:', err);
}
// Arm the watchdog. Called at boot AND every time we show a "signing/loading"
// spinner (renderAuthLoading), because onAuthChange firing with `null` at boot
// clears the first arm — the post-sign-in hang (auth callback's Firestore reads
// wedging on a locked-down browser) happens AFTER that, so it needs re-arming.
function armBootWatchdog() {
  clearTimeout(_bootTimer);
  // 30s, not 12s. The auth callback's first Firestore read is now time-boxed at
  // 8s + a 10s retry (auth.js readUserDocWithRetry), so a genuine failure
  // surfaces on its own well inside this window. A 12s watchdog was FIRING ON
  // SLOW-BUT-WORKING logins — it replaced the whole app with the error page at
  // 12s while the read was still in flight, so a teacher whose read landed at
  // 14s was stuck on an error that was no longer true.
  _bootTimer = setTimeout(showBootDiagnostic, 30000);
}
function settleBoot() { clearTimeout(_bootTimer); _bootTimer = null; }
armBootWatchdog();

// === Auth Gate ===
onAuthChange(async (user, _role) => {
  // Reaching here is a settled state (login screen for null, data load for a
  // user) — stand the watchdog down. The data load below has its own 25s
  // timeout; a later sign-in re-arms via renderAuthLoading.
  settleBoot();
  if (!user) {
    lastRoutedUid = null;
    renderLogin();
    return;
  }
  // Avoid re-running the data load + route on every token refresh / scope add
  // for the same user.
  if (lastRoutedUid === user.uid && initialized) {
    return;
  }
  lastRoutedUid = user.uid;
  _realRole = getCurrentRole();
  // Apply test role from sessionStorage if set. The full "Test as any role"
  // preview stays an OWNER tool. The ONE exception: the CRT preview ("Preview as
  // CRT" from the Admin → CRT panel) is available to the full-admin tier
  // (owner / ld_leader / principal) so top-level staff can test the CRT setup
  // and see the sandboxed CRT view. Purely client-side — server access is
  // unchanged, so this can never escalate anything.
  const testRole = sessionStorage.getItem('_testRole');
  const crtPreviewOk = testRole === 'crt' && ['owner', 'ld_leader', 'principal'].includes(_realRole);
  if (testRole && (_realRole === 'owner' || crtPreviewOk)) {
    __setTestRole(testRole);
  } else if (testRole) {
    sessionStorage.removeItem('_testRole'); // clear a stale/ineligible test role
  }
  // Test-as-USER — triple-gated: PRACTICE projects only, real role must be
  // owner, AND the signed-in account must be a vendor MASTER. This means no
  // school-side account (not even a school's own owner) can impersonate a real
  // user, and it is impossible on production. Purely client-side either way
  // (real Firebase Auth is unchanged), so it can never escalate server access.
  _testUserInfo = null;
  if (IS_PRACTICE && _realRole === 'owner' && isMasterUser()) {
    try { _testUsers = await loadUsers(); } catch { _testUsers = []; }
    const tuid = sessionStorage.getItem('_testUserId');
    if (tuid) {
      const tu = _testUsers.find(u => u.id === tuid || u.uid === tuid);
      if (tu) {
        applyTestUser(tu);
        _testUserInfo = { id: tu.id, name: tu.displayName || tu.email || 'user', role: tu.role || 'all_teachers', email: tu.email || '' };
      } else {
        sessionStorage.removeItem('_testUserId'); // stale - user was removed
      }
    }
  } else if (sessionStorage.getItem('_testUserId')) {
    sessionStorage.removeItem('_testUserId'); // not eligible here - clear it
  }
  setSentryUser(user.uid);

  // CRT logins are fully sandboxed: they can't read /students (firestore.rules
  // denies them), so skip the whole data load and mount ONLY the CRT daily
  // report page. Any attempt to navigate elsewhere is bounced back to /crt.
  if (isCrt()) {
    initialized = true;
    const { renderCrtPage } = await import('./pages/crt.js');
    const mount = () => { if (!window.location.hash.startsWith('#/crt')) { window.location.hash = '#/crt'; return; } renderCrtPage(app); };
    window.addEventListener('hashchange', mount);
    if (!window.location.hash.startsWith('#/crt')) window.location.hash = '#/crt';
    else renderCrtPage(app);
    return;
  }

  // Load initial data
  try {
    // FAIL FAST: sign-in already proved Firestore is unreachable (both the
    // user-doc read AND its retry timed out → authStage 'firestore-unreachable').
    // Without this we'd queue another 25s of reads that are certain to time out,
    // leaving the user staring at a spinner for ~43s before any message. Surface
    // the real diagnosis immediately instead.
    const bootErr = getBootConnectError();
    if (bootErr) {
      throw new Error(
        'Can’t reach the database. Sign-in worked, but this network is blocking the '
        + 'connection to Firestore. This is usually a school/work network, VPN, or '
        + 'security software. Try a mobile hotspot to confirm.',
      );
    }
    app.innerHTML = '<div class="loading-screen"><div class="loading-spinner"></div><p>Loading data...</p></div>';
    const [students, enrolments, settings, config, signatures, staff, classes, assessmentSchedule, users] = await Promise.all([
      withTimeout(loadStudents(), 25000, 'Loading students timed out (25s). Your connection or the school network may be blocking the database.'),
      withTimeout(loadEnrolments(), 25000, 'Loading enrolments timed out (25s). Your connection or the school network may be blocking the database.'),
      withTimeout(loadSettings(), 25000, 'Loading school settings timed out (25s). Your connection or the school network may be blocking the database.'),
      loadAllConfig().catch(() => null), // config may not be seeded yet
      loadSignatures().catch(() => []),
      loadStaff().catch(() => []),
      loadClasses().catch(() => []),
      loadAssessmentSchedule().catch(() => ({})),
      loadUsers().catch(() => []),
    ]);
    window.nccd.students = students;
    window.nccd.enrolments = enrolments;
    window.nccd.staff = staff;
    window.nccd.classes = classes;
    // Users double as the staff roster in the unified people model.
    window.nccd.users = users;
    window.nccd.assessmentSchedule = assessmentSchedule || {};
    window.nccd.settings = settings;
    applyAiEnabled();
    window.nccd.config = config;
    // Merge per-disorder trackers into the runtime config so they're
    // available even if Firestore hasn't been re-seeded yet. The seed
    // pushes the same data so re-seeded projects are a no-op here.
    try {
      const local = (await import('./data/funnel-data-full.js')).default;
      if (window.nccd.config?.trackers) {
        window.nccd.config.trackers.trackers = window.nccd.config.trackers.trackers || {};
        Object.entries(local.trackers || {}).forEach(([tid, tracker]) => {
          // Only inject `dx_*` trackers — never override an existing
          // imputed-bucket tracker that the admin may have edited.
          if (tid.startsWith('dx_') && !window.nccd.config.trackers.trackers[tid]) {
            window.nccd.config.trackers.trackers[tid] = tracker;
          }
        });
        // disorderToTracker: ONLY inject the local default if Firestore
        // doesn't have an entry for that disorder. Once the user has
        // edited mappings in Settings, Firestore wins absolutely — no
        // merge with local defaults that would resurrect deleted picks.
        window.nccd.config.trackers.disorderToTracker = window.nccd.config.trackers.disorderToTracker || {};
        Object.entries(local.disorderToTracker || {}).forEach(([did, ids]) => {
          if (did in window.nccd.config.trackers.disorderToTracker) return; // user has touched this disorder - respect their picks
          window.nccd.config.trackers.disorderToTracker[did] = Array.isArray(ids) ? ids.slice() : [];
        });
      }
    } catch (e) {
      console.warn('Per-disorder tracker merge skipped:', e?.message);
    }
    window.nccd.signatures = signatures;
    // Load the current user's personal signature (non-blocking).
    loadMySignature(user.uid).then(sig => {
      window.nccd.mySignature = sig || null;
      setCurrentUserSignature(sig);
    }).catch(() => { window.nccd.mySignature = null; });
    // Anyone holding cap_signatures (Owner / LD Leader / Executive by default,
    // or a Custom user granted it) can pick a colleague's saved signature from
    // the dropdown — load them all in the background. Everyone else still gets
    // "Use mine" from their own /users doc.
    if (canUseSignatureLibrary()) {
      // Derive the feed from the users roster loaded above — the SAME data,
      // no second query. loadAllUserSignatures re-queried /users and could
      // fail/return empty independently (custom users with cap_signatures saw
      // an empty dropdown while the roster held 22 signatures, 29 Aug 2026).
      window.nccd.userSignatures = (users || [])
        .filter(u => u.signatureImage)
        .map(u => ({
          id: 'user_' + (u.id || u.uid),
          name: u.signatureName || u.displayName || u.email || 'User',
          image: u.signatureImage,
          role: u.role ? String(u.role).replace(/_/g, ' ') : '',
          source: 'user',
        }));
      // Keep the original loader as a late self-check: if it returns MORE than
      // the roster-derived list (shouldn't happen), prefer it.
      loadAllUserSignatures().then(list => {
        if ((list || []).length > window.nccd.userSignatures.length) window.nccd.userSignatures = list;
      }).catch(() => { /* roster-derived feed already in place */ });
    } else {
      window.nccd.userSignatures = [];
    }
    // Pending platform-config updates published from nccd-admin —
    // Leadership / Owner only see the banner because they're the only
    // roles that can apply (and they're the ones the schools nominate
    // to make catalogue-level decisions).
    if (isLeadership()) {
      import('./services/platform-config.js')
        .then(m => m.loadPendingPlatformUpdates())
        .then(pending => { if (pending?.length) showPlatformUpdateBanner(pending); })
        .catch(() => {});
    }
    // Load interventions in background (non-blocking)
    import('./services/students.js').then(m => m.loadInterventions?.()).then(ivs => { window.nccd.interventions = ivs || []; }).catch(() => { window.nccd.interventions = []; });
    import('./services/students.js').then(m => m.loadSessions?.()).then(s => { window.nccd.sessions = s || []; }).catch(() => { window.nccd.sessions = []; });
    // Apply school theme colours if configured
    if (settings.theme) applyTheme(settings.theme); // { funnel, disorders, trackers, meetingTypes } or null

    // Set active year
    const cur = getCurrentYear();
    const years = settings.years || [];
    window.nccd.activeYear = years.includes(cur) ? cur : (years[years.length - 1] || cur);

    // Active data collection period (Aug–Jul NCCD window). Separate dimension
    // from the academic year — leadership advances it via Settings → Data, and
    // it never changes year levels. Falls back to the date-derived period.
    const { currentCollectionPeriod } = await import('./services/collection-period.js');
    window.nccd.activeCollectionPeriod = settings.collectionPeriod || currentCollectionPeriod();
    window.nccd.collectionPeriods = Array.isArray(settings.collectionPeriods) && settings.collectionPeriods.length
      ? settings.collectionPeriods
      : [window.nccd.activeCollectionPeriod];
    // The VIEW period is SESSION-ONLY, always starting at the CURRENT census
    // window. It used to persist per-device (localStorage 'cpView') — which
    // meant a teacher who once flipped it on the classroom machine saw
    // period-filtered records "missing" there forever while their laptop
    // showed them: the last deterministic "same record visible here, gone
    // there" mechanism (decided with Harry, 15 Sep 2026, during the Sam
    // vanishing-saves investigation). Flip it when you need history; every
    // fresh load returns to today's period.
    window.nccd.viewCollectionPeriod = window.nccd.activeCollectionPeriod || '';
    try { localStorage.removeItem('cpView'); } catch { /* ignore */ }
    // One-time "new collection period" banner (set by the manual rollover, the
    // owner test button, or the automatic August-deadline Cloud Function).
    window.nccd.collectionPeriodNotice = settings.collectionPeriodNotice || null;
    maybeShowCollectionPeriodNotice();

    if (!initialized) {
      setupRoutes();
      initRouter();
      initialized = true;
    } else {
      // Re-trigger current route on subsequent auth changes
      const { handleRoute } = await import('./router.js');
      if (typeof handleRoute === 'function') handleRoute();
    }
    if (!window.location.hash || window.location.hash === '#') {
      navigate('/records');
    }
    // First-run? Show onboarding wizard.
    setTimeout(maybeShowOnboarding, 250);
  } catch (err) {
    // A boot timeout on the default STREAMING transport is the blocked-proxy
    // signature (the user-doc read may squeak through where the bulk reads
    // stall). Flip this browser to sticky long-polling and reload — the next
    // boot uses plain HTTPS and gets through. No-op (falls through to the
    // error panel) when already on long-polling or freshly reloaded.
    if (/timed out|timeout/i.test(String(err?.message || err))) {
      const { fallBackToLongPolling } = await import('./firebase.js');
      if (fallBackToLongPolling('boot data load timed out')) return;
    }
    // Never fail silently. Show a loud red banner with the friendly message AND
    // the raw technical detail (code + message) so a user can read it back to
    // us over the phone — that's how we diagnose a spinning login remotely.
    const techDetail = esc([err?.code, err?.message].filter(Boolean).join(' - ') || String(err));
    const userEmail = esc(getCurrentUser()?.email || '');
    app.innerHTML = `
      <div class="login-error-wrap">
        <div class="login-error-banner" role="alert">
          <strong>⚠ Couldn't load your data</strong>
          <p>${esc(friendlyError(err, "Something stopped your data from loading."))}</p>
          <p class="login-error-tech">What went wrong (please read this to support): <code>${techDetail}</code></p>
          ${userEmail ? `<p class="login-error-tech">Signed in as: <code>${userEmail}</code></p>` : ''}
          <details class="login-error-full"><summary>Show full technical error</summary><pre>${esc(fullErrorReport(err))}</pre></details>
        </div>
        <div class="login-error-actions">
          <button class="btn btn-primary" id="btnDataLoadRetry">Retry</button>
          <button class="btn btn-secondary" id="btnDataLoadSignout">Sign out &amp; try again</button>
        </div>
      </div>`;
    app.querySelector('#btnDataLoadRetry')?.addEventListener('click', () => location.reload());
    app.querySelector('#btnDataLoadSignout')?.addEventListener('click', async () => {
      try { await logout(); } catch { /* ignore */ }
      location.reload();
    });
    console.error('[EASY] Data load failed:', err);
  }
});

function getCurrentYear() {
  const y = new Date().getFullYear();
  return String(y - 1).slice(-2) + '/' + String(y).slice(-2);
}

// Leadership-only banner shown when nccd-admin has published platform
// config updates the school hasn't applied yet. Click Review → modal
// listing each pending catalogue; Apply copies the staged doc into the
// local /config/{id} and bumps appliedVersion so the banner stops
// reappearing.
function showPlatformUpdateBanner(pending) {
  if (!pending?.length) return;
  if (document.getElementById('platformUpdateBanner')) return; // idempotent
  const fmt = (count) => count === 1 ? '1 platform update' : `${count} platform updates`;
  const labelById = {
    disorders: 'Disorders catalogue',
    trackers: 'Adjustment trackers',
    funnel: 'NCCD categories + frequency keys',
    meetingTypes: 'Meeting / plan templates',
    noteCategories: 'Staff-note categories',
    goals: 'Goal domains + achievement levels',
    mtss: 'MTSS triangle (conditions / categories)',
    behaviourRating: 'Behaviour-rating statements',
    fbaTemplate: 'FBA template',
  };
  const bar = document.createElement('div');
  bar.id = 'platformUpdateBanner';
  bar.className = 'platform-update-banner';
  bar.innerHTML = `
    <span class="platform-update-banner-icon">🆕</span>
    <span class="platform-update-banner-text">${fmt(pending.length)} from Easy Clarity 4 Growth platform available - review &amp; apply.</span>
    <button class="btn btn-sm" id="platformUpdateReview">Review</button>
    <button class="btn btn-sm platform-update-banner-dismiss" id="platformUpdateDismiss" title="Dismiss for this session - reappears on next sign-in">✕</button>`;
  document.body.insertBefore(bar, document.body.firstChild);
  document.getElementById('platformUpdateDismiss').addEventListener('click', () => bar.remove());
  document.getElementById('platformUpdateReview').addEventListener('click', () => openPlatformUpdateModal(pending, bar));
}

// One-time banner announcing a new NCCD collection period. Shows once per
// period per browser (dismissal remembered in localStorage keyed by period),
// for everyone — it's an informational filter-dimension change, not an action.
async function maybeShowCollectionPeriodNotice() {
  const notice = window.nccd?.collectionPeriodNotice;
  if (!notice || !notice.period) return;
  const { collectionPeriodShort } = await import('./services/collection-period.js');
  const period = String(notice.period);
  const seenKey = `cpNoticeSeen_${period}`;
  try { if (localStorage.getItem(seenKey)) return; } catch { /* private mode */ }
  if (document.getElementById('cpNoticeBanner')) return; // idempotent
  const auto = String(notice.kind || '').startsWith('auto');
  const bar = document.createElement('div');
  bar.id = 'cpNoticeBanner';
  bar.className = 'cp-notice-banner';
  bar.innerHTML = `
    <span class="cp-notice-icon">📅</span>
    <span class="cp-notice-text">${auto
      ? 'The NCCD submission deadline passed - you\'re now collecting for '
      : 'New NCCD collection period started: '}<strong>${collectionPeriodShort(period)}</strong>. New notes and meetings now count toward it. To look back at last period, use the 📅 picker in the top bar.</span>
    <button class="btn btn-sm cp-notice-dismiss" id="cpNoticeDismiss" title="Dismiss">✕ Got it</button>`;
  document.body.insertBefore(bar, document.body.firstChild);
  document.getElementById('cpNoticeDismiss')?.addEventListener('click', () => {
    try { localStorage.setItem(seenKey, '1'); } catch { /* ignore */ }
    bar.remove();
  });
}

function openPlatformUpdateModal(pending, bar) {
  const labelById = {
    disorders: 'Disorders catalogue',
    trackers: 'Adjustment trackers',
    funnel: 'NCCD categories + frequency keys',
    meetingTypes: 'Meeting / plan templates',
    noteCategories: 'Staff-note categories',
    goals: 'Goal domains + achievement levels',
    mtss: 'MTSS triangle (conditions / categories)',
    behaviourRating: 'Behaviour-rating statements',
    fbaTemplate: 'FBA template',
  };
  const fmtDate = (v) => {
    if (!v) return 'unknown date';
    const d = typeof v === 'object' && v?.toDate ? v.toDate() : new Date(v);
    return isNaN(d.getTime()) ? 'unknown date' : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  };
  const overlay = document.createElement('div');
  overlay.className = 'modal-backdrop platform-update-modal-backdrop';
  overlay.innerHTML = `
    <div class="modal platform-update-modal">
      <h2>Platform updates</h2>
      <p class="text-muted">Updates published from the Easy Clarity 4 Growth admin console. Applying replaces your local catalogue with the published version - any edits made in Settings → Configuration since the last publish will be lost.</p>
      <div class="platform-update-list">
        ${pending.map(p => `
          <div class="platform-update-row" data-id="${p.id}">
            <div class="platform-update-row-main">
              <div class="platform-update-row-title">${labelById[p.id] || p.id}</div>
              <div class="platform-update-row-meta">v${p.stagedVersion} · published ${fmtDate(p.publishedAt)} by ${p.publishedBy || '-'} · you're on v${p.appliedVersion}</div>
            </div>
            <button class="btn btn-primary btn-sm platform-update-apply" data-id="${p.id}">Apply</button>
          </div>`).join('')}
      </div>
      <div class="modal-actions">
        <button class="btn" id="platformUpdateClose">Close</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.getElementById('platformUpdateClose').addEventListener('click', () => overlay.remove());

  overlay.querySelectorAll('.platform-update-apply').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.id;
      if (!confirm(`Apply ${labelById[id] || id} update? Local edits to this catalogue will be replaced.`)) return;
      btn.disabled = true;
      btn.textContent = 'Applying…';
      try {
        const { applyPlatformUpdate } = await import('./services/platform-config.js');
        const email = (getCurrentUser()?.email || '').toLowerCase() || null;
        await applyPlatformUpdate(id, email);
        // Remove the row + collapse banner if everything's been applied.
        overlay.querySelector(`.platform-update-row[data-id="${id}"]`)?.remove();
        if (!overlay.querySelector('.platform-update-row')) {
          overlay.remove();
          bar?.remove();
          // Tell the user a reload picks up the new catalogue everywhere.
          alert('Platform update applied. Refresh to pick up the new catalogue across the app.');
        }
      } catch (e) {
        btn.disabled = false;
        btn.textContent = 'Apply';
        alert(friendlyError(e, 'Something went wrong. Please try again.'));
      }
    });
  });
}

const LOGIN_PROVIDERS_KEY = 'easyAuthProviders';
// Last-known-good providers from a previous visit — used to paint the login
// buttons INSTANTLY and correctly, so a slow/failed pre-auth config read can't
// flash (or get stuck on) the wrong set. Returns null if never cached.
function cachedLoginProviders() {
  try {
    const c = JSON.parse(localStorage.getItem(LOGIN_PROVIDERS_KEY) || 'null');
    if (Array.isArray(c) && c.length) return c;
  } catch { /* ignore */ }
  return null;
}
// Resilient fresh read of /public/authConfig. The login screen is PRE-AUTH, and
// the App Check token may not be minted yet on first paint — so a single read
// can be rejected, which previously fell back to showing BOTH providers even
// for a Google-only school. Retry a few times before giving up, and cache any
// success so future visits are instant + correct.
//   → returns string[]  : providers explicitly configured
//   → returns null       : config read OK but no providers set (use default both)
//   → returns undefined  : every read failed (keep cached/default)
async function fetchLoginProviders() {
  const { getDoc, doc } = await import('firebase/firestore');
  const { db } = await import('./firebase.js');
  for (let i = 0; i < 3; i++) {
    try {
      const snap = await getDoc(doc(db, 'public', 'authConfig'));
      const p = snap.exists() ? snap.data().providers : null;
      if (Array.isArray(p) && p.length) {
        try { localStorage.setItem(LOGIN_PROVIDERS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
        return p;
      }
      return null; // config present but no providers → caller uses default both
    } catch {
      await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  return undefined; // all reads failed
}

async function renderLogin() {
  const paint = (providers) => {
    const googleBtn = `<button class="btn btn-primary btn-lg login-btn" id="btnLoginGoogle">
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/></svg>
            <span>Sign in with Google</span>
          </button>`;
    const microsoftBtn = `<button class="btn btn-outline btn-lg login-btn" id="btnLoginMicrosoft">
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><rect x="1" y="1" width="7.5" height="7.5" fill="#F25022"/><rect x="9.5" y="1" width="7.5" height="7.5" fill="#7FBA00"/><rect x="1" y="9.5" width="7.5" height="7.5" fill="#00A4EF"/><rect x="9.5" y="9.5" width="7.5" height="7.5" fill="#FFB900"/></svg>
            <span>Sign in with Microsoft</span>
          </button>`;
    app.innerHTML = `
    <div class="login-screen">
      <img src="/easy-logo.png?v=2" class="login-brand-logo" alt="Easy Clarity 4 Growth">
      <div class="login-card">
        <p class="brand-tagline">Evidence And Support for You</p>
        <p>Sign in with your school account</p>
        <div class="login-error-banner login-error-inline" id="loginErrorBanner" role="alert" hidden></div>
        <div class="login-buttons">
          ${providers.includes('google') ? googleBtn : ''}
          ${providers.includes('microsoft') ? microsoftBtn : ''}
        </div>
      </div>
      <div class="login-security-badge">
        <a href="https://app.aikido.dev/audit-report/external/tuw26lVR3eZso5jXzk2Aszgf/request" target="_blank" rel="noopener noreferrer">
          <img src="https://app.aikido.dev/assets/badges/full-light-theme.svg" alt="Aikido Security Audit Report" height="36">
        </a>
      </div>
      <p class="login-copyright">&copy; ${COPYRIGHT_YEAR} ${COPYRIGHT_OWNER}. All rights reserved.</p>
    </div>
  `;
    document.getElementById('btnLoginGoogle')?.addEventListener('click', async () => {
      try {
        // ONLY show the "Signing you in…" bridge when someone actually signed
        // in. login() resolves normally when the user cancels the popup, so
        // assuming success here left the spinner up forever waiting for an
        // onAuthChange that never fires — and the boot watchdog then reported it
        // as the school network blocking the database. Cancelling must simply
        // return you to the login card.
        if (await login()) renderAuthLoading();
      } catch (e) { showLoginError(e); }
    });
    document.getElementById('btnLoginMicrosoft')?.addEventListener('click', async () => {
      try {
        const { loginMicrosoft } = await import('./auth.js');
        if (await loginMicrosoft()) renderAuthLoading();
      } catch (e) {
        showLoginError(e);
      }
    });
  };

  // Loud, in-page login error — never a silent failure or a fleeting alert().
  // Shows the friendly message plus the raw code/message so the user can read
  // the exact error back to us when a sign-in spins or dies.
  function showLoginError(e) {
    const banner = document.getElementById('loginErrorBanner');
    if (!banner) { alert(friendlyError(e, 'Sign-in failed. Please try again.')); return; }
    const tech = esc([e?.code, e?.message].filter(Boolean).join(' - ') || String(e));
    banner.innerHTML = `<strong>⚠ Sign-in failed</strong>
      <p>${esc(friendlyError(e, 'Sign-in failed. Please try again.'))}</p>
      <p class="login-error-tech">What went wrong (please read this to support): <code>${tech}</code></p>
      <details class="login-error-full"><summary>Show full technical error</summary><pre>${esc(fullErrorReport(e))}</pre></details>`;
    banner.hidden = false;
    console.error('[EASY] Sign-in failed:', e);
  }

  // Paint instantly with the last-known-good set (or both as the safe default),
  // then reconcile with a fresh, retrying read — re-painting only if it changed.
  let providers = cachedLoginProviders() || ['google', 'microsoft'];
  paint(providers);
  const fresh = await fetchLoginProviders();
  const resolved = Array.isArray(fresh) && fresh.length
    ? fresh
    : (fresh === null ? ['google', 'microsoft'] : providers); // null=default, undefined=keep
  if (resolved.slice().sort().join(',') !== providers.slice().sort().join(',')) paint(resolved);
}

// Shown for the brief moment between the OAuth popup closing and the auth-state
// change firing the data load — otherwise the login card lingers and reads as an
// error. Replaced by "Loading data…" (then the app) as soon as onAuthChange runs.
function renderAuthLoading() {
  app.innerHTML = '<div class="loading-screen"><div class="loading-spinner"></div><p>Signing you in…</p></div>';
  // Re-arm the watchdog: after this point the auth callback runs Firestore reads
  // that can hang on a locked-down browser. If onAuthChange doesn't fire within
  // the timeout, show the diagnostic instead of an endless "Signing you in…".
  armBootWatchdog();
}

function setupRoutes() {
  route('/records', renderRecordsPage);
  route('/admin', renderAdminPage);
  route('/appointments', renderAppointmentsPageWrapper);
  route('/student', renderStudentPage);
  route('/settings', renderSettingsPage);
  route('/eso', renderEsoPage);
  route('/monitoring', renderMonitoringPageWrapper);
  route('/assessments', renderAssessmentsPageWrapper);
  route('/ai', renderAIPage);
  route('/interventions', renderInterventionsPageWrapper);
  route('/library', renderLibraryPageWrapper);
  route('/referrals-inbox', renderReferralsInboxPageWrapper);
  route('/refer-staff', () => renderReferralFormPageWrapper('staff'));
  route('/refer-parent', () => renderReferralFormPageWrapper('parent'));
  route('/refer-student', () => renderReferralFormPageWrapper('student'));
  route('/support-triangles', renderSupportTrianglesPageWrapper);
  route('/crt-reports', renderCrtReportsPageWrapper);
  route('/my-profile', renderMyProfilePageWrapper);
}

// Optional school-configured quick link (shared drive / SharePoint / intranet).
// Generic — the label is whatever the school sets in Settings → School Details.
function quickLinkHtml() {
  const s = window.nccd?.settings || {};
  // New array shape first; legacy single pair as fallback for schools that
  // haven't re-saved Settings since multiple links shipped.
  const links = (Array.isArray(s.quickLinks) && s.quickLinks.length
    ? s.quickLinks
    : (s.quickLinkUrl ? [{ label: s.quickLinkLabel, url: s.quickLinkUrl }] : []))
    .filter(l => l && /^https?:\/\//i.test(l.url || ''));
  return links.map(l => {
    const label = l.label || 'Quick link';
    return `<a class="header-quick-link" href="${esc(l.url)}" target="_blank" rel="noopener" title="${esc(label)} (opens in a new tab)">🔗 ${esc(label)}</a>`;
  }).join('');
}

/** Re-render the quick-link buttons after Settings saves new links. */
export function refreshHeaderQuickLink() {
  if (typeof document === 'undefined') return;
  const host = document.querySelector('.header-user');
  if (!host) return;
  host.querySelectorAll('.header-quick-link').forEach(el => el.remove());
  const html = quickLinkHtml();
  if (html) host.insertAdjacentHTML('afterbegin', html);
}

// Census-year period key → display span, e.g. '2027' → '26/27'. The NCCD
// period straddles two calendar years so it always DISPLAYS as a span; the
// academic year stays a single flat year. (Sync twin of collectionPeriodSpan —
// inlined so the header renders without waiting on a dynamic import.)
function _cpSpan(p) {
  const y = parseInt(p, 10);
  if (isNaN(y)) return String(p || '');
  return `${String(y - 1).slice(-2)}/${String(y).slice(-2)}`;
}

// NCCD collection-period filter — sits beside the year selector's role in the
// header. "All periods" shows everything; picking a period filters DATED
// content (notes, meetings, monitoring, goals, docs) to that census window.
function cpFilterHtml() {
  const active = window.nccd?.activeCollectionPeriod || '';
  const view = window.nccd?.viewCollectionPeriod || '';
  const periods = Array.isArray(window.nccd?.collectionPeriods) && window.nccd.collectionPeriods.length
    ? window.nccd.collectionPeriods.slice() : (active ? [active] : []);
  const opts = Array.from(new Set(periods)).sort().reverse()
    .map(p => `<option value="${esc(p)}" ${p === view ? 'selected' : ''}>NCCD ${esc(_cpSpan(p))}</option>`).join('');
  return `<label class="header-cp-badge${view ? ' is-filtering' : ''}" title="Filter dated content by NCCD collection period">📅
    <select id="cpViewSelect" class="header-cp-select" data-no-dirty aria-label="Filter by NCCD collection period">
      <option value="">All periods</option>${opts}
    </select></label>`;
}

function renderHeader() {
  const user = getCurrentUser();
  const role = getCurrentRole();
  const logo = window.nccd.settings.logo || '';
  const roleLabels = { ...ROLE_LABELS, all_teachers: 'Teacher', grade_teacher: 'Grade Teacher', crt: 'CRT (casual)' };
  const roleName = roleLabels[role] || role;
  const isTesting = _realRole !== role || !!_testUserInfo;

  return `
    ${logo ? `<div class="app-school-banner"><img src="${esc(logo)}" class="header-school-logo" alt=""></div>` : ''}
    <header class="header">
      <div class="header-left">
        <img src="/easy-logo-navbar.png?v=2" class="header-easy-logo" alt="Easy Clarity 4 Growth">
      </div>
      <nav class="header-nav">
        <a href="#/records" class="nav-link ${window.location.hash.includes('/records') ? 'active' : ''}">Records</a>
        ${canViewReferrals() ? `<div class="nav-dropdown">
          <button type="button" class="nav-link nav-refer-btn" id="referMenuBtn" aria-haspopup="true" aria-expanded="false">Referrals <span class="nav-refer-arrow" aria-hidden="true">▾</span></button>
          <div class="nav-refer-menu" id="referMenu" role="menu" hidden>
            ${canViewReferralsInbox() ? '<a href="#/referrals-inbox" class="nav-refer-item nav-refer-inbox" role="menuitem">Referrals inbox</a>' : ''}
            <a href="#/refer-staff" class="nav-refer-item" role="menuitem">Staff referral</a>
            <a href="#/refer-parent" class="nav-refer-item" role="menuitem">Parent / carer referral</a>
            <a href="#/refer-student" class="nav-refer-item" role="menuitem">Student - “I need some help”</a>
          </div>
        </div>` : ''}
        ${canAccessSettings() ? '<a href="#/admin" class="nav-link ' + (window.location.hash.includes('/admin') ? 'active' : '') + '">Admin</a>' : ''}
        ${canViewAssessments() ? '<a href="#/assessments" class="nav-link ' + (window.location.hash.includes('/assessments') ? 'active' : '') + '">Assessments</a>' : ''}
        ${canViewMonitoring() ? '<a href="#/monitoring" class="nav-link ' + (window.location.hash.includes('/monitoring') ? 'active' : '') + '">Daily Monitoring</a>' : ''}
        ${canViewInterventionsNav() ? '<a href="#/interventions" class="nav-link ' + (window.location.hash.includes('/interventions') ? 'active' : '') + '">Interventions</a>' : ''}
        ${canViewAppointments() ? '<a href="#/appointments" class="nav-link ' + (window.location.hash.includes('/appointments') ? 'active' : '') + '">Appointments</a>' : ''}
        ${canViewTimetablesNav() ? '<a href="#/eso" class="nav-link ' + (window.location.hash.includes('/eso') ? 'active' : '') + '">Timetables</a>' : ''}
        ${canViewLibrary() ? '<a href="#/library" class="nav-link ' + (window.location.hash.includes('/library') ? 'active' : '') + '">Library</a>' : ''}
        ${canUseCohortAi() && !canAccessSettings() && isAiOn() ? '<a href="#/ai" class="nav-link ' + (window.location.hash.includes('/ai') ? 'active' : '') + '">✨ AI Insights</a>' : ''}
      </nav>
      <div class="header-user">
        ${quickLinkHtml()}
        ${cpFilterHtml()}
        <button type="button" id="helpBtn" class="header-help-btn" title="Help &amp; tutorials" aria-label="Help and tutorials">
          <span class="header-help-icon" aria-hidden="true">?</span><span class="header-help-text">Help</span>
        </button>
        <button id="userMenuBtn" class="user-avatar-btn ${isTesting ? 'is-testing' : ''}" aria-haspopup="true" aria-expanded="false" title="${esc(user?.email || '')}">
          <span class="user-avatar-initials">${esc((user?.email || '?').slice(0, 1).toUpperCase())}</span>
          ${isTesting ? '<span class="user-avatar-dot" title="Testing as another user/role"></span>' : ''}
        </button>
        <div id="userMenu" class="user-menu" role="menu" hidden>
          <div class="user-menu-header">
            <div class="user-menu-email">${esc(user?.email || '')}</div>
            <div class="user-menu-role">
              <span class="user-role-badge">${_testUserInfo ? '🧪 ' + esc(_testUserInfo.name) + ' - ' + esc(roleName) : esc(roleName) + (isTesting ? ' (testing)' : '')}</span>
            </div>
          </div>
          <a href="#/my-profile" class="user-menu-item">My Profile</a>
          ${canAccessSettings() ? `<a href="#/settings" class="user-menu-item ${window.location.hash.includes('/settings') ? 'active' : ''}">Settings</a>` : ''}
          ${_realRole === 'owner' ? `<div class="user-menu-section">
            <div class="user-menu-label">Test as</div>
            ${IS_PRACTICE && isMasterUser() ? `<select id="testUserSwitch" class="test-role-switch ${_testUserInfo ? 'is-active' : ''}" aria-label="Test as a specific user">
              <option value="">- user -</option>
              ${_testUsers.slice().sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')).map(u => `<option value="${esc(u.id || u.uid)}" ${_testUserInfo && _testUserInfo.id === (u.id || u.uid) ? 'selected' : ''}>${esc(u.displayName || u.email || 'user')} - ${esc(roleLabels[u.role] || u.role || 'teacher')}</option>`).join('')}
            </select>` : ''}
            <select id="testRoleSwitch" class="test-role-switch ${(_realRole !== role && !_testUserInfo) ? 'is-active' : ''}">
              <option value="">- role -</option>
              ${Object.entries(roleLabels).map(([k, v]) => `<option value="${k}" ${role === k && _realRole !== k && !_testUserInfo ? 'selected' : ''}>${v}</option>`).join('')}
            </select>
            ${isTesting ? '<button type="button" class="user-menu-item test-back-btn" id="testBackBtn">↩ Back to owner</button>' : ''}
          </div>` : ''}
          <button class="user-menu-item user-menu-logout" id="btnLogout">Logout</button>
        </div>
      </div>
    </header>
  `;
}

function renderFooter() {
  return `
    <footer class="app-footer">
      <span>&copy; ${COPYRIGHT_YEAR} ${COPYRIGHT_OWNER}. All rights reserved.</span>
      <span class="app-footer-sep" aria-hidden="true">&middot;</span>
      <span>Easy Clarity 4 Growth &mdash; including its software, website content and layout, the support triangle, templates and standard library materials &mdash; is proprietary. Unauthorised copying, distribution or use is prohibited.</span>
    </footer>
  `;
}

function renderPage(content) {
  // Mount the page via DOMParser instead of innerHTML — every page's
  // render*() function escapes user data with esc(), so the resulting
  // string is already safe; using DOMParser+replaceChildren just means
  // the path doesn't cross an innerHTML setter, which keeps Aikido happy.
  const testBanner = _testUserInfo
    ? `<div class="test-user-banner" role="status">🧪 Viewing as <strong>${esc(_testUserInfo.name)}</strong> (${esc(ROLE_LABELS[_testUserInfo.role] || _testUserInfo.role)}) - this is a preview of what they see. <button type="button" id="testBannerBack" class="test-user-banner-back">Back to owner</button></div>`
    : '';
  const html = '<a href="#mainContent" class="skip-link">Skip to main content</a>' + renderHeader() + testBanner + '<main id="mainContent" class="main-content" tabindex="-1">' + content + '</main>' + renderFooter();
  const parsed = new DOMParser().parseFromString(`<!doctype html><body>${html}`, 'text/html');
  app.replaceChildren(...Array.from(parsed.body.childNodes));
  document.getElementById('btnLogout')?.addEventListener('click', logout);
  // Test role switcher — role-only preview (all owner sessions)
  document.getElementById('testRoleSwitch')?.addEventListener('change', (e) => {
    const val = e.target.value;
    if (!val) return;
    sessionStorage.removeItem('_testUserId'); // role + user tests are mutually exclusive
    sessionStorage.setItem('_testRole', val);
    location.reload();
  });
  // Test-as-USER switcher (practice only) — impersonate a specific real user
  document.getElementById('testUserSwitch')?.addEventListener('change', (e) => {
    const val = e.target.value;
    if (!val) return;
    sessionStorage.removeItem('_testRole');
    sessionStorage.setItem('_testUserId', val);
    location.reload();
  });
  // Back to owner — clears whichever test overlay is active (menu + banner)
  const backToOwner = () => {
    sessionStorage.removeItem('_testRole');
    sessionStorage.removeItem('_testUserId');
    location.reload();
  };
  document.getElementById('testBackBtn')?.addEventListener('click', backToOwner);
  document.getElementById('testBannerBack')?.addEventListener('click', backToOwner);

  // User-menu dropdown — click avatar to open, click outside or pick an
  // item to close. Stop the avatar's own click from immediately bubbling
  // to the outside-click handler that closes it.
  const menuBtn = document.getElementById('userMenuBtn');
  const menu = document.getElementById('userMenu');
  if (menuBtn && menu) {
    const closeMenu = () => { menu.hidden = true; menuBtn.setAttribute('aria-expanded', 'false'); };
    const openMenu = () => { menu.hidden = false; menuBtn.setAttribute('aria-expanded', 'true'); };
    menuBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (menu.hidden) openMenu(); else closeMenu();
    });
    // Close on outside click, on Escape, and on any in-menu link click.
    document.addEventListener('click', (e) => {
      if (!menu.hidden && !menu.contains(e.target) && e.target !== menuBtn) closeMenu();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !menu.hidden) closeMenu(); });
    menu.querySelectorAll('a.user-menu-item').forEach(a => a.addEventListener('click', () => closeMenu()));
  }

  // "Refer ▾" nav dropdown — links to the standalone referral forms.
  const referBtn = document.getElementById('referMenuBtn');
  const referMenu = document.getElementById('referMenu');
  if (referBtn && referMenu) {
    const close = () => { referMenu.hidden = true; referBtn.setAttribute('aria-expanded', 'false'); };
    referBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (referMenu.hidden) { referMenu.hidden = false; referBtn.setAttribute('aria-expanded', 'true'); } else close();
    });
    document.addEventListener('click', (e) => { if (!referMenu.hidden && !referMenu.contains(e.target) && e.target !== referBtn) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !referMenu.hidden) close(); });
    referMenu.querySelectorAll('a').forEach(a => a.addEventListener('click', () => close()));
  }

  // Collection-period filter → set the view period + re-render the current page.
  const cpSel = document.getElementById('cpViewSelect');
  if (cpSel) {
    cpSel.addEventListener('change', async () => {
      window.nccd.viewCollectionPeriod = cpSel.value || '';
      // Deliberately NOT persisted — the view period is session-only and
      // resets to the current census window on every load (see the boot
      // comment where 'cpView' used to be restored).
      cpSel.closest('.header-cp-badge')?.classList.toggle('is-filtering', !!cpSel.value);
      const { handleRoute } = await import('./router.js');
      handleRoute(); // re-render current view under the new period filter
    });
  }

  // Help button → open the Help Center (videos + step-by-step guides). Lazy
  // import so the help content + modal only load when a user actually opens it.
  const helpBtn = document.getElementById('helpBtn');
  if (helpBtn) {
    helpBtn.addEventListener('click', async () => {
      const { openHelpCenter } = await import('./components/help-center.js');
      openHelpCenter();
    });
  }
}

// === Page Renderers ===
// Mount a code-split page: show the spinner, dynamically import + render the
// page module, then wire logout. Crucially, if the import or render throws —
// most commonly a STALE CHUNK after a deploy (the loaded index.html points at
// chunk filenames the server has since replaced, so import() 404s) — we replace
// the spinner with a friendly, actionable panel instead of leaving it spinning
// forever. `loader(host)` does the import + render.
// Exported for the rebuilt pages (src/v2/shell.js): they mount into the same page area.
export async function mountLazyPage(loader) {
  renderPage('<div id="pageContent"><div class="loading-screen"><div class="loading-spinner"></div></div></div>');
  const host = document.getElementById('pageContent');
  try {
    await loader(host);
  } catch (err) {
    const msg = friendlyError(err, "This section couldn't be opened. Please refresh the page and try again.");
    if (host) {
      host.replaceChildren();
      const box = document.createElement('div');
      box.className = 'empty-state';
      const h = document.createElement('h3'); h.textContent = 'Please refresh';
      const p = document.createElement('p'); p.textContent = msg;
      const btn = document.createElement('button'); btn.className = 'btn btn-primary'; btn.textContent = 'Refresh the page';
      btn.addEventListener('click', () => location.reload());
      box.append(h, p, btn);
      host.appendChild(box);
    }
    return;
  }
  document.getElementById('btnLogout')?.addEventListener('click', logout);
}

function renderRecordsPage() {
  return mountLazyPage(async (host) => {
    const { renderRecordsPage: render } = await import('./pages/records.js');
    await render(host);
  });
}

function renderAdminPage() {
  return mountLazyPage(async (host) => {
    const { renderAdminPage: render } = await import('./pages/admin.js');
    await render(host);
  });
}

function renderStudentPage(params) {
  const studentId = params?.[0];
  const tab = params?.[1] || 'notes';
  return mountLazyPage(async (host) => {
    const { renderStudentDetailPage: render } = await import('./pages/student-detail.js');
    await render(host, studentId, tab);
  });
}

function renderSettingsPage() {
  return mountLazyPage(async (host) => {
    const { renderSettingsPage: render } = await import('./pages/settings.js');
    await render(host);
  });
}

function renderEsoPage() {
  return mountLazyPage(async (host) => {
    if (!canViewTimetablesNav()) { host.innerHTML = noAccessHtml('Timetables'); return; }
    const { renderEsoTimetablePage } = await import('./pages/eso-timetable.js');
    await renderEsoTimetablePage(host);
  });
}

function renderInterventionsPageWrapper() {
  return mountLazyPage(async (host) => {
    if (!canViewInterventionsNav()) { host.innerHTML = noAccessHtml('Interventions'); return; }
    const { renderInterventionsHubPage } = await import('./pages/interventions-hub.js');
    await renderInterventionsHubPage(host);
  });
}

function renderCrtReportsPageWrapper() {
  return mountLazyPage(async (host) => {
    // CRT day reports carry per-student ratings and free-text notes. They are
    // for the class's own teacher and for leadership — nobody else. The route
    // has no nav link but was reachable by URL.
    if (!isLeadershipOrAdmin() && !getOwnClassIds().length) { host.innerHTML = noAccessHtml('CRT Reports'); return; }
    const { renderCrtReportsPage } = await import('./pages/crt-reports.js');
    await renderCrtReportsPage(host);
  });
}

function renderAppointmentsPageWrapper() {
  return mountLazyPage(async (host) => {
    if (!canViewAppointments()) {
      host.innerHTML = '<div class="page-narrow"><div class="empty-state"><h2>Appointments</h2><p>You don\'t have access to Appointments. Ask your school leadership if you think you should.</p></div></div>';
      return;
    }
    const { renderAppointmentsPage } = await import('./pages/appointments.js');
    await renderAppointmentsPage(host);
  });
}

function noAccessHtml(title) {
  return `<div class="page-narrow"><div class="empty-state"><h2>${title}</h2><p>You don't have access to ${title}. Ask your school leadership if you think you should.</p></div></div>`;
}

function renderLibraryPageWrapper() {
  return mountLazyPage(async (host) => {
    if (!canViewLibrary()) { host.innerHTML = noAccessHtml('Library'); return; }
    const { renderLibraryPage } = await import('./pages/library.js');
    await renderLibraryPage(host);
  });
}

function renderMonitoringPageWrapper() {
  return mountLazyPage(async (host) => {
    if (!canViewMonitoring()) { host.innerHTML = noAccessHtml('Daily Monitoring'); return; }
    // The rebuilt Daily Monitoring page (src/v2/pages/monitoring). The old
    // page file stays: the student Behaviour Monitor tab reuses its report.
    const { mountMonitoringPage } = await import('./v2/pages/monitoring/index.js');
    return mountMonitoringPage();
    // eslint-disable-next-line no-unreachable
    const { renderMonitoringPage } = await import('./pages/monitoring.js');
    await renderMonitoringPage(host);
  });
}

function renderAssessmentsPageWrapper() {
  return mountLazyPage(async (host) => {
    if (!canViewAssessments()) { host.innerHTML = noAccessHtml('Assessments'); return; }
    const { renderAssessmentsPage } = await import('./pages/assessments.js');
    await renderAssessmentsPage(host);
  });
}

function renderSupportTrianglesPageWrapper() {
  return mountLazyPage(async (host) => {
    // Reachable by URL even though it has no nav link — guard it like every
    // other page. Roles with no Home Triangle zone (specialist, ESO, most
    // custom users) previously got the whole school's triangle list here.
    if (sectionAccess('supportTriangle') === 'none') { host.innerHTML = noAccessHtml('Support Triangles'); return; }
    const { renderSupportTrianglesPage } = await import('./pages/support-triangles.js');
    await renderSupportTrianglesPage(host);
  });
}

function renderReferralsInboxPageWrapper() {
  return mountLazyPage(async (host) => {
    if (!canViewReferralsInbox()) { host.innerHTML = noAccessHtml('Referrals'); return; }
    const { renderReferralsInboxPage } = await import('./pages/referrals-inbox.js');
    await renderReferralsInboxPage(host);
  });
}

function renderReferralFormPageWrapper(variant = 'staff') {
  return mountLazyPage(async (host) => {
    const { renderReferralFormPage } = await import('./pages/referral-form.js');
    await renderReferralFormPage(host, variant);
  });
}

function renderMyProfilePageWrapper() {
  return mountLazyPage(async (host) => {
    const { renderMyProfilePage } = await import('./pages/my-profile.js');
    await renderMyProfilePage(host);
  });
}

// AI master switch. Two levels:
//   1. aiAllowed — set by Easy Clarity 4 Growth Admin (nccd-admin). If false, the school CANNOT
//      use AI at all and can't turn it on themselves.
//   2. aiEnabled — the school's own choice (Settings toggle), only meaningful
//      when allowed.
// Effective AI = allowed AND enabled. When off, a body.ai-disabled class hides
// every .ai-btn* control app-wide; the nav link + /ai route are gated below.
export function isAiOn() {
  const s = window.nccd?.settings || {};
  // Practice/demo projects lock AI to the owner so a full roster of onboarded
  // test users can't run up real Gemini costs. Live schools (e.g. St Brigid's)
  // are unaffected — there AI is governed purely by their settings flags.
  if (IS_PRACTICE && _realRole !== 'owner') return false;
  return s.aiAllowed !== false && s.aiEnabled !== false;
}
export function applyAiEnabled() {
  const enabled = isAiOn();
  if (typeof document !== 'undefined') document.body.classList.toggle('ai-disabled', !enabled);
  return enabled;
}

function renderAIPage() {
  // Cohort AI Insights is admin/leadership-tier (or a custom user explicitly
  // granted the AI 'cohort' sub-action). Guard the page itself, not just the link.
  if (!canUseCohortAi()) {
    renderPage('<div class="ai-page"><h2>AI Insights</h2><div class="settings-card"><p>You don\'t have access to the AI Insights page. Ask a leader if you need it.</p></div></div>');
    document.getElementById('btnLogout')?.addEventListener('click', logout);
    return;
  }
  if (!isAiOn()) {
    const disallowed = window.nccd?.settings?.aiAllowed === false;
    const msg = disallowed
      ? 'AI features are not enabled for your school by Easy Clarity 4 Growth Admin. Contact Easy Clarity 4 Growth to enable them.'
      : 'AI features are currently turned off for your school. A leader can re-enable them in <a href="#/settings">Settings</a>.';
    renderPage(`<div class="ai-page"><a href="#/admin" class="btn btn-outline btn-sm ai-back-admin">&larr; Back to Admin</a><h2>AI Insights</h2><div class="settings-card"><p>${msg}</p></div></div>`);
    document.getElementById('btnLogout')?.addEventListener('click', logout);
    return;
  }
  renderPage(`
    <div class="ai-page">
      <a href="#/admin" class="btn btn-outline btn-sm ai-back-admin">&larr; Back to Admin</a>
      <h2>AI Insights</h2>
      <p class="text-muted ai-page-intro">AI analysis across your NCCD data. Every student, parent, and staff name is fuzzy-aliased client-side before being sent to the model, and unaliased on return. The model never sees a real name.</p>

      <div id="aiQuotaBanner" class="ai-quota-banner" hidden></div>

      <div class="settings-card ai-cohort-card">
        <h3>Cohort scope</h3>
        <p class="text-muted ai-cohort-hint">Narrow the scope and the buttons below get more individual - whole school gives cohort-level themes, a single class gives per-student detail.</p>
        <div class="ai-cohort-row">
          <label>Year level:
            <select id="aiYearLevel" class="ai-cohort-select">
              <option value="">All year levels</option>
              ${(() => {
                // Only show year levels the user is scoped to.
                const visibleIds = new Set(getVisibleStudents(window.nccd.students || []).map(s => s.id));
                const yls = (window.nccd.enrolments || [])
                  .filter(e => visibleIds.has(e.studentId))
                  .map(e => e.yearLevel)
                  .filter((v, i, a) => v && a.indexOf(v) === i)
                  .sort();
                return yls.map(yl => `<option value="${yl}">${yl}</option>`).join('');
              })()}
            </select>
          </label>
          <label>Class:
            <select id="aiClass" class="ai-cohort-select">
              <option value="">All classes</option>
            </select>
          </label>
          <span class="text-muted" id="aiCohortCount">-</span>
        </div>
      </div>

      <div class="ai-prompt-grid">
        <div class="settings-card ai-prompt-card" data-tone="funding">
          <div class="ai-prompt-card-icon" aria-hidden="true">📋</div>
          <h3>NCCD Submission Review</h3>
          <p class="text-muted ai-prompt-card-blurb">One consolidated review of the scope's strengths and areas of weakness - folds the old year-level, focus, funding, trend, risk and consultation scans into a single read.</p>
          <button id="btnAiSubmissionReview" class="ai-btn ai-btn-lg">✨ Generate</button>
        </div>
        <div class="settings-card ai-prompt-card ai-prompt-card-flagship" data-tone="readiness">
          <div class="ai-prompt-card-icon" aria-hidden="true">🏁</div>
          <h3>Individual NCCD Readiness</h3>
          <p class="text-muted ai-prompt-card-blurb">Runs a nuanced per-student readiness scan across everyone in scope (meetings, docs, adjustments - not just tick counts). Results appear on the <strong>Records page</strong>.</p>
          <button id="btnAiReadiness" class="ai-btn ai-btn-lg">✨ Run scan</button>
        </div>
        <div class="settings-card ai-prompt-card" data-tone="goals">
          <div class="ai-prompt-card-icon" aria-hidden="true">🎯</div>
          <h3>Goals Across Cohort</h3>
          <p class="text-muted ai-prompt-card-blurb">An ESO view of who is working on what. Whole-school shows themes; pick a class or year level to see each student's individual goals.</p>
          <button id="btnAiGoals" class="ai-btn ai-btn-lg">✨ Generate</button>
        </div>
        <div class="settings-card ai-prompt-card" data-tone="handover">
          <div class="ai-prompt-card-icon" aria-hidden="true">🎒</div>
          <h3>Year Handover</h3>
          <p class="text-muted ai-prompt-card-blurb">End-of-year starter notes for next year's teachers, organised by class within each year level.</p>
          <button id="btnAiHandover" class="ai-btn ai-btn-lg">✨ Generate</button>
        </div>
        <div class="settings-card ai-prompt-card" data-tone="risk">
          <div class="ai-prompt-card-icon" aria-hidden="true">✅</div>
          <h3>NCCD Submission List</h3>
          <p class="text-muted ai-prompt-card-blurb">Who's ready to submit vs borderline vs not ready - each with imputed/diagnosed, level and category.</p>
          <button id="btnAiSubmissionList" class="ai-btn ai-btn-lg">✨ Generate</button>
        </div>
        <div class="settings-card ai-prompt-card" data-tone="assessment">
          <div class="ai-prompt-card-icon" aria-hidden="true">📊</div>
          <h3>Assessment Overview</h3>
          <p class="text-muted ai-prompt-card-blurb">See how the scope is tracking on one assessment - who's below / at / above benchmark, trends, and who hasn't sat it yet.</p>
          <label class="ai-assess-picker-label">Assessment:
            <select id="aiAssessmentType" class="ai-assess-picker"><option value="">Choose an assessment…</option></select>
          </label>
          <input id="aiAssessmentOther" class="ai-assess-picker ai-assess-other" type="text" placeholder="Type the assessment name" autocomplete="off" hidden>
          <button id="btnAiAssessmentOverview" class="ai-btn ai-btn-lg">✨ Generate</button>
        </div>
      </div>

      <div id="aiResultPanel" class="settings-card ai-result-panel">
        <div class="ai-result-header">
          <h3 id="aiResultTitle">Result</h3>
          <div class="ai-result-header-actions">
            <button class="btn btn-outline btn-sm is-hidden-by-condition" id="aiResultWord">Download Word</button>
            <button class="btn btn-outline btn-sm" id="aiResultPrint">🖨 Print</button>
            <button class="btn btn-outline btn-sm" id="aiResultClose">Close</button>
          </div>
        </div>
        <div id="aiResultBody" class="ai-result-body"></div>
      </div>

      <div class="settings-card ai-warning-card">
        <strong>⚠ Always review before acting.</strong>
        <span class="ai-warning-card-body">AI suggestions are advisory only and must be verified against actual evidence. Never use AI output to inflate or fabricate funding claims.</span>
      </div>
    </div>
  `);
  bindAIPage();
}

// Meeting-schedule planner moved to components/meeting-schedule.js and now
// lives at the bottom of the APPOINTMENTS page (booking happens there).

function bindAIPage() {
  const ylSelect = document.getElementById('aiYearLevel');
  const classSelect = document.getElementById('aiClass');
  const cohortLabel = document.getElementById('aiCohortCount');
  const panel = document.getElementById('aiResultPanel');
  const titleEl = document.getElementById('aiResultTitle');
  const bodyEl = document.getElementById('aiResultBody');

  // Year Handover — a teacher's own personal note per student. Kept in
  // localStorage (keyed by class + student) so it survives re-renders, reopening
  // the handover, and prints alongside the AI briefing. Delegated on the
  // persistent result body, which outlives each innerHTML swap.
  const HANDOVER_NOTES_KEY = 'nccd_handover_notes';
  const loadHandoverNotes = () => { try { return JSON.parse(localStorage.getItem(HANDOVER_NOTES_KEY) || '{}'); } catch { return {}; } };
  const saveHandoverNote = (k, v) => {
    const m = loadHandoverNotes();
    if (v) m[k] = v; else delete m[k];
    try { localStorage.setItem(HANDOVER_NOTES_KEY, JSON.stringify(m)); } catch { /* quota */ }
  };
  bodyEl.addEventListener('click', (e) => {
    const btn = e.target.closest('.ai-handover-note-btn');
    if (!btn) return;
    const wrap = btn.closest('.ai-handover-note');
    const ta = wrap?.querySelector('.ai-handover-note-input');
    if (!ta) return;
    wrap.classList.add('is-open');
    ta.focus();
  });
  bodyEl.addEventListener('input', (e) => {
    const ta = e.target.closest('.ai-handover-note-input');
    if (ta) saveHandoverNote(ta.dataset.hkey, ta.value.trim());
  });

  // Quota banner — render at top once the annual usage doc has loaded.
  (async () => {
    const banner = document.getElementById('aiQuotaBanner');
    if (!banner) return;
    const { getAiQuotaSummary } = await import('./services/ai.js');
    const q = await getAiQuotaSummary();
    if (!q || q.pct < 60) return; // healthy use, no banner
    const fmt = (n) => Math.round(n / 1000).toLocaleString() + 'K';
    const tone = q.exhausted ? 'is-error' : q.warned80 ? 'is-error' : 'is-warn';
    const heading = q.exhausted
      ? 'AI features are paused for your school'
      : q.warned80
      ? `You've used ${q.pct.toFixed(0)}% of this year's AI capacity`
      : `Heads up, ${q.pct.toFixed(0)}% of this year's AI capacity used`;
    const body = q.exhausted
      ? `You've reached the annual AI quota of ${fmt(q.cap)} tokens. Contact <a href="mailto:${q.contact}">${q.contact}</a> to raise the cap, usually back up within a few hours.`
      : `Used ${fmt(q.tokens)} of ${fmt(q.cap)} tokens this year. If you're heading into a busy NCCD period and expect to need more, get in touch with <a href="mailto:${q.contact}">${q.contact}</a> to extend your allowance.`;
    banner.hidden = false;
    banner.className = 'ai-quota-banner ' + tone;
    banner.innerHTML = `<strong>${heading}</strong><div class="ai-quota-banner-body">${body}</div>`;
  })();

  function getCohort() {
    const yl = ylSelect.value;
    const classId = classSelect?.value || '';
    const activeYear = window.nccd.activeYear;
    const enrolments = (window.nccd.enrolments || []).filter(e =>
      e.academicYear === activeYear
      && (!yl || e.yearLevel === yl)
      && (!classId || e.classId === classId));
    const ids = new Set(enrolments.map(e => e.studentId));
    // Always strip students outside this user's scope before passing to AI
    // or before counting — they should not see other students' names appear
    // in summaries or readiness lists.
    const visible = getVisibleStudents(window.nccd.students || []);
    return visible.filter(s => ids.has(s.id));
  }

  // True when the user has narrowed to a single class or year level — the
  // cohort buttons switch to a more individual/per-student view at this point.
  function isScoped() { return !!(ylSelect.value || (classSelect && classSelect.value)); }
  // Human label for the current scope, embedded in AI prompts + result titles.
  function scopeLabel() {
    const yl = ylSelect.value;
    const clsName = classSelect && classSelect.value
      ? (classSelect.options[classSelect.selectedIndex]?.textContent || '').trim()
      : '';
    if (clsName) return yl ? `${yl} - ${clsName}` : clsName;
    if (yl) return yl;
    return 'the whole school';
  }

  // Populate the Class dropdown — classes for the active year, optionally
  // filtered to the chosen year level. Rebuilt whenever the year changes.
  function populateClasses() {
    if (!classSelect) return;
    const yl = ylSelect.value;
    const activeYear = window.nccd.activeYear;
    // Classes carry a SET of year levels (yearLevels[]) so a composite
    // "Prep/1" class shows under either year filter.
    const classes = (window.nccd.classes || [])
      .filter(c => c.academicYear === activeYear && (!yl || (c.yearLevels || []).includes(yl) || c.yearLevel === yl))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const keep = classSelect.value;
    classSelect.innerHTML = '<option value="">All classes</option>'
      + classes.map(c => `<option value="${c.id}">${c.name || 'Unnamed class'}</option>`).join('');
    // Preserve the selection if it still exists under the new year filter.
    if (keep && classes.some(c => c.id === keep)) classSelect.value = keep;
  }

  function updateCohortLabel() {
    const c = getCohort();
    cohortLabel.textContent = `${c.length} student${c.length === 1 ? '' : 's'} in scope`;
  }
  ylSelect?.addEventListener('change', () => { populateClasses(); updateCohortLabel(); });
  classSelect?.addEventListener('change', updateCohortLabel);
  populateClasses();
  updateCohortLabel();

  // Plain-text result (errors, free-form fallback when the model didn't
  // return structured output).
  // The Year Handover on screen, kept for the Word download.
  let lastHandover = null;
  function showResult(title, text, isError = false) {
    lastHandover = null;
    document.getElementById('aiResultWord')?.classList.add('is-hidden-by-condition');
    titleEl.textContent = title;
    bodyEl.textContent = text;
    bodyEl.classList.toggle('ai-result-body-error', !!isError);
    bodyEl.classList.remove('ai-result-body-structured');
    panel.classList.add('is-visible');
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const escA = (s) => { const d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; };

  // Reveal the result panel with pre-built HTML (renderers below escape every
  // AI-returned string via escA before interpolation).
  function showPanel(title, html, wordResult = null) {
    lastHandover = wordResult;
    document.getElementById('aiResultWord')?.classList.toggle('is-hidden-by-condition', !wordResult);
    titleEl.textContent = title;
    bodyEl.classList.add('ai-result-body-structured');
    bodyEl.classList.remove('ai-result-body-error');
    bodyEl.innerHTML = html;
    panel.classList.add('is-visible');
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // === Distinct per-button renderers — each AI button has its own layout. ===

  function renderSubmissionReview(r) {
    const statusMap = { on_track: ['🟢', 'On track', 'good'], attention: ['🟡', 'Needs attention', 'warn'], at_risk: ['🔴', 'At risk', 'risk'] };
    const st = statusMap[r.overallStatus] || statusMap.attention;
    let h = `<div class="ai-review-banner ai-review-${st[2]}"><span class="ai-review-status">${st[0]} ${st[1]}</span><p class="ai-review-headline">${escA(r.headline || '')}</p></div>`;
    const rd = r.readiness;
    if (rd) {
      // readiness values are now per-category NAME lists (older cached results
      // may still be plain counts — handle both).
      const cell = (v) => Array.isArray(v)
        ? { count: v.length, names: v.filter(Boolean) }
        : { count: (v ?? null), names: [] };
      const tile = (cls, label, v) => {
        const c = cell(v);
        const names = c.names.length
          ? `<ul class="ai-kpi-names">${c.names.map(n => `<li>${escA(n)}</li>`).join('')}</ul>`
          : '';
        return `<div class="ai-kpi-tile ${cls}"><div class="ai-kpi-value">${escA(c.count ?? '-')}</div><div class="ai-kpi-label">${label}</div>${names}</div>`;
      };
      h += `<div class="ai-kpi-grid ai-kpi-grid-named">
        ${tile('ai-kpi-good', 'Ready', rd.ready)}
        ${tile('ai-kpi-warn', 'Borderline', rd.borderline)}
        ${tile('ai-kpi-risk', 'At risk', rd.atRisk)}
      </div>`;
    }
    const strengths = (r.strengths || []).filter(Boolean);
    const weaknesses = (r.weaknesses || []).filter(Boolean);
    h += '<div class="ai-review-cols">';
    h += '<div class="ai-review-col ai-review-col-strength"><h4>✅ Strengths</h4>';
    h += strengths.length ? strengths.map(s => `<div class="ai-review-item"><strong>${escA(s.title)}</strong><p>${escA(s.detail)}</p></div>`).join('') : '<p class="text-muted">None identified.</p>';
    h += '</div><div class="ai-review-col ai-review-col-weak"><h4>⚠️ Areas of weakness</h4>';
    h += weaknesses.length ? weaknesses.map(w => { const sev = ['high', 'medium', 'low'].includes(w.severity) ? w.severity : 'medium'; return `<div class="ai-review-item ai-sev-${sev}"><span class="ai-sev-badge">${sev}</span><strong>${escA(w.title)}</strong><p>${escA(w.detail)}</p></div>`; }).join('') : '<p class="text-muted">None identified.</p>';
    h += '</div></div>';
    const trends = (r.trends || []).filter(Boolean);
    if (trends.length) h += `<h4 class="ai-section-h">Trends</h4><ul class="ai-rec-list">${trends.map(t => `<li>${escA(t)}</li>`).join('')}</ul>`;
    const recs = (r.recommendations || []).filter(Boolean);
    if (recs.length) h += `<h4 class="ai-section-h">Recommended actions</h4><ol class="ai-rec-list">${recs.map(t => `<li>${escA(t)}</li>`).join('')}</ol>`;
    return h;
  }

  function renderGoalsCohort(r) {
    let h = `<p class="ai-result-headline">Goals across ${escA(r.scopeLabel || 'the cohort')}</p>`;
    const themes = (r.themes || []).filter(Boolean);
    if (themes.length) {
      h += '<div class="ai-goal-themes">';
      h += themes.map(t => `<div class="ai-goal-theme"><span class="ai-goal-theme-count">${escA(t.count ?? (t.students || []).length)}</span><div><strong>${escA(t.theme)}</strong><div class="text-muted u-text-xs">${escA((t.students || []).join(', '))}</div></div></div>`).join('');
      h += '</div>';
    }
    const students = (r.students || []).filter(Boolean);
    h += '<div class="ai-goal-students">';
    h += students.map(s => {
      let c = `<div class="ai-goal-card"><div class="ai-goal-card-head"><strong>${escA(s.name)}</strong><span class="text-muted">${escA([s.yearLevel, s.className].filter(Boolean).join(' · '))}</span></div>`;
      if (s.focus) c += `<p class="ai-goal-focus">${escA(s.focus)}</p>`;
      (s.goals || []).forEach(g => { const stt = ['on_track', 'emerging', 'needs_attention'].includes(g.status) ? g.status : 'emerging'; c += `<div class="ai-goal-row ai-goalstatus-${stt}"><span class="ai-goal-area">${escA(g.area)}</span><span class="ai-goal-goal">${escA(g.goal)}</span></div>`; });
      if (s.esoNote) c += `<p class="ai-goal-eso">🧑‍🏫 ${escA(s.esoNote)}</p>`;
      return c + '</div>';
    }).join('');
    h += '</div>';
    if (!students.length && !themes.length) h += '<p class="text-muted">No goal data found for this scope.</p>';
    return h;
  }

  function renderAssessmentOverview(r) {
    const c = r.counts || {};
    let h = `<p class="ai-result-headline">${escA(r.assessment || 'Assessment')} - ${escA(r.scopeLabel || 'the cohort')}</p>`;
    if (r.overview) h += `<p class="ai-assess-overview">${escA(r.overview)}</p>`;
    const tiles = [
      ['Assessed', c.assessed, 'a'], ['Not assessed', c.notAssessed, 'na'],
      ['Below', c.below, 'below'], ['At benchmark', c.at, 'at'], ['Above', c.above, 'above'],
    ];
    h += '<div class="ai-assess-counts">' + tiles.map(([label, val, cls]) =>
      `<div class="ai-assess-tile ai-assess-${cls}"><div class="ai-assess-tile-val">${escA(val ?? '-')}</div><div class="ai-assess-tile-label">${label}</div></div>`).join('') + '</div>';
    const statusLabel = { not_assessed: 'Not assessed', below: 'Below', at: 'At', above: 'Above' };
    const students = (r.students || []).filter(Boolean);
    if (students.length) {
      h += '<div class="ai-assess-table-wrap"><table class="data-table ai-assess-table"><thead><tr><th>Student</th><th>Year / Class</th><th>Status</th><th>Score</th><th>Expected</th><th>Note</th></tr></thead><tbody>';
      students.forEach(s => {
        const st = ['not_assessed', 'below', 'at', 'above'].includes(s.status) ? s.status : 'at';
        // Was "+12m" / "-18m" from monthsVsBenchmark — an interpolated figure no
        // publisher validated. The school's own expected score says the same
        // thing truthfully, next to the actual score. See expectationLabel().
        const expTxt = (s.expected == null || s.expected === '') ? '-' : String(s.expected);
        h += `<tr class="ai-assess-row-${st}"><td>${escA(s.name)}</td><td class="text-muted">${escA([s.yearLevel, s.className].filter(Boolean).join(' · '))}</td><td><span class="ai-assess-badge ai-assess-badge-${st}">${statusLabel[st]}</span></td><td>${escA(s.score ?? '-')}</td><td>${st === 'not_assessed' ? '-' : escA(expTxt)}</td><td>${escA(s.note || '')}</td></tr>`;
      });
      h += '</tbody></table></div>';
    }
    const na = (r.notAssessed || []).filter(Boolean);
    if (na.length) h += `<p class="ai-assess-na-list"><strong>Not yet assessed (${na.length}):</strong> ${escA(na.join(', '))}</p>`;
    if (!students.length && !na.length) h += '<p class="text-muted">No results found for this assessment in the selected scope.</p>';
    return h;
  }

  function renderYearHandover(r) {
    const classes = (r.classes || []).filter(Boolean);
    if (!classes.length) return '<p class="text-muted">No students in scope.</p>';
    const savedNotes = loadHandoverNotes();
    return classes.map(c => {
      const head = [c.yearLevel, c.className].filter(Boolean).join(' - ') || 'Class';
      const students = (c.students || []).map(s => {
        let b = `<div class="ai-handover-student"><strong>${escA(s.name)}</strong><p>${escA(s.snapshot)}</p><ul class="ai-handover-bullets">`;
        if (s.strengths) b += `<li><b>Strengths:</b> ${escA(s.strengths)}</li>`;
        if (s.watchFor) b += `<li><b>Watch for:</b> ${escA(s.watchFor)}</li>`;
        if (s.adjustmentsThatWork) b += `<li><b>What works:</b> ${escA(s.adjustmentsThatWork)}</li>`;
        b += '</ul>';
        const hkey = `${head}::${s.name}`;
        const note = savedNotes[hkey] || '';
        b += `<div class="ai-handover-note ${note ? 'is-open' : ''}">
          <button type="button" class="ai-handover-note-btn">✏️ Add personal note</button>
          <label class="ai-handover-note-label">📝 Your personal note for next year's teacher</label>
          <textarea class="ai-handover-note-input" data-hkey="${escA(hkey)}" rows="2" placeholder="Anything the AI wouldn't know - your own observations, what to try first, parent rapport…">${escA(note)}</textarea>
        </div>`;
        return b + '</div>';
      }).join('');
      return `<div class="ai-handover-class"><h4>🎒 ${escA(head)}</h4>${students}</div>`;
    }).join('');
  }

  function renderSubmissionList(data) {
    const STATUS = { imputed: 'Imputed', diagnosed: 'Diagnosed', both: 'Imputed/Diagnosed' };
    const LVL = { qdtp: 'QDTP', supplementary: 'Supplementary', substantial: 'Substantial', extensive: 'Extensive' };
    const rowFor = (s, showMissing) => {
      const stKey = String(s.status || '').toLowerCase();
      const badgeCls = stKey === 'diagnosed' ? 'diagnosed' : 'imputed';
      const missing = showMissing && (s.missing || []).length ? `<span class="ai-sub-reason">Missing: ${escA((s.missing || []).join(', '))}</span>` : (s.reason ? `<span class="ai-sub-reason">${escA(s.reason)}</span>` : '');
      return `<div class="ai-sub-row">
        <span class="ai-sub-name">${escA(s.name)}</span>
        <span class="badge badge-status-${badgeCls} ai-sub-badge">${escA(STATUS[stKey] || s.status || '-')}</span>
        <span class="ai-sub-meta">${escA(LVL[String(s.level || '').toLowerCase()] || s.level || '-')}</span>
        <span class="ai-sub-meta">${escA(s.category || '-')}</span>
        ${missing}
      </div>`;
    };
    const section = (cls, icon, label, list, showMissing) => `<div class="ai-sub-section ${cls}"><h4>${icon} ${label} <span class="ai-sub-count">${(list || []).length}</span></h4>${(list || []).length ? list.map(s => rowFor(s, showMissing)).join('') : '<p class="text-muted">None.</p>'}</div>`;
    let h = section('ai-sub-ready', '✅', 'Ready to submit', data.ready, false);
    h += section('ai-sub-borderline', '🟡', 'Borderline', data.borderline, true);
    if ((data.notReady || []).length) h += section('ai-sub-notready', '🔴', 'Not ready', data.notReady, true);
    return h;
  }
  document.getElementById('aiResultClose')?.addEventListener('click', () => { panel.classList.remove('is-visible'); });
  document.getElementById('aiResultWord')?.addEventListener('click', async (e) => {
    if (!lastHandover) return;
    const btn = e.currentTarget;
    btn.disabled = true;
    try {
      const { handoverDocx } = await import('./utils/handover-docx.js');
      const title = document.getElementById('aiResultTitle')?.textContent || 'Year Handover';
      const blob = await handoverDocx(title, lastHandover, loadHandoverNotes());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^\w -]+/g, '').trim() || 'Year Handover'}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Handover Word download failed:', err);
      const { toast } = await import('./components/toast.js');
      toast(friendlyError(err, 'The Word document could not be made. Please try again.'));
    } finally {
      btn.disabled = false;
    }
  });
  document.getElementById('aiResultPrint')?.addEventListener('click', async () => {
    // Print JUST the result panel. This used to open a popup and write an inline
    // <style> block on top of <link>ed stylesheets - the links loaded (same
    // origin) but the inline overrides were blocked by the CSP, so the print
    // came out half-styled. printInPlace() renders into the current document,
    // where utilities.css already applies in full.
    const title = document.getElementById('aiResultTitle')?.textContent || 'AI Result';
    const bodyEl = document.getElementById('aiResultBody');
    if (!bodyEl) return;
    const { printInPlace } = await import('./utils/print-in-place.js');
    printInPlace(`<div class="airp"><h1>${esc(title)}</h1>${bodyEl.innerHTML}</div>`);
  });

  // Build the rich per-student cohort bundles, enriched with year level +
  // class name so the goals / handover prompts can group correctly. Fetches
  // each student's /profile (goals, tracker, etc.) so every button can reason
  // nuanced over ALL of a student's data, not just the spreadsheet record.
  async function buildBundles(cohort, extraOpts = {}) {
    const { buildCohortStudentBundle } = await import('./utils/ai-bundle.js');
    const { loadProfile } = await import('./services/students.js');
    const { enrolmentFor, classNameForStudent } = await import('./services/class-helpers.js');
    const cachedReadinessByStudent = window.nccd?.profileCache || {};
    const fetched = await Promise.all(cohort.map(s => loadProfile(s.id).catch(() => null)));
    const profileById = Object.fromEntries(cohort.map((s, i) => [s.id, fetched[i]]));
    return cohort.map(s => {
      const enr = enrolmentFor(s.id);
      const b = buildCohortStudentBundle(s, {
        sessions: window.nccd.sessions || [],
        interventions: window.nccd.interventions || [],
        library: window.nccd.library || [],
        enrolments: window.nccd.enrolments || [],
        activeYear: window.nccd.activeYear,
        schoolName: window.nccd.settings?.name || '',
        allStudents: window.nccd.students || [],
        cachedReadiness: cachedReadinessByStudent[s.id]?.summary?.readiness || null,
        profile: profileById[s.id] || null,
        assessmentType: extraOpts.assessmentType || '',
      });
      b.yearLevel = enr?.yearLevel || b.yearLevel || '';
      b.className = classNameForStudent(s, enr) || '';
      return b;
    });
  }

  // Shared runner for the four structured cohort buttons: builds bundles,
  // calls the AI fn, renders with the button's own renderer.
  async function runStructured(label, btn, aiFn, renderer, extraOpts = {}) {
    const cohort = getCohort();
    if (!cohort.length) { import('./components/toast.js').then(m => m.toast('No students in scope')); return; }
    // Tolerate a missing button (e.g. a stale/null reference) — the scan
    // should still run rather than crash before it starts.
    const originalLabel = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = `⏳ Analysing ${cohort.length}…`; }
    try {
      const bundles = await buildBundles(cohort, extraOpts);
      const result = await aiFn(bundles, { scopeLabel: scopeLabel(), scoped: isScoped(), ...extraOpts });
      showPanel(`${label} - ${scopeLabel()}`, renderer(result), renderer === renderYearHandover ? result : null);
    } catch (err) {
      console.error('AI insight failed:', err);
      const friendly = friendlyError(err, 'The assistant could not finish this one. Please try again, or narrow the scope to a single class or year level.');
      showResult(`${label} - couldn’t finish`, friendly, true);
      const { toast } = await import('./components/toast.js');
      toast(friendly);
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = originalLabel; }
    }
  }

  // NOTE: capture `e.currentTarget` into `btn` BEFORE any `await`. The browser
  // nulls out event.currentTarget once the handler yields at an await, so
  // passing `e.currentTarget` *after* `await import(...)` handed runStructured a
  // null button — that threw "Cannot read properties of null (reading
  // 'textContent')" and was why only the readiness scan (which reads
  // currentTarget synchronously) appeared to work.
  document.getElementById('btnAiSubmissionReview')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    const { aiSubmissionReview } = await import('./services/ai.js');
    runStructured('NCCD Submission Review', btn, aiSubmissionReview, renderSubmissionReview);
  });
  document.getElementById('btnAiGoals')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    const { aiGoalsCohort } = await import('./services/ai.js');
    runStructured('Goals Across Cohort', btn, aiGoalsCohort, renderGoalsCohort);
  });
  document.getElementById('btnAiHandover')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    const { aiYearHandover } = await import('./services/ai.js');
    runStructured('Year Handover', btn, aiYearHandover, renderYearHandover);
  });
  document.getElementById('btnAiSubmissionList')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    const { aiSubmissionList } = await import('./services/ai.js');
    runStructured('NCCD Submission List', btn, aiSubmissionList, renderSubmissionList);
  });
  document.getElementById('btnAiAssessmentOverview')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    const sel = document.getElementById('aiAssessmentType');
    let assessmentType = sel?.value || '';
    if (assessmentType === '__other__') assessmentType = (document.getElementById('aiAssessmentOther')?.value || '').trim();
    if (!assessmentType) {
      const { toast } = await import('./components/toast.js');
      toast('Choose an assessment first (e.g. Westwood Addition)');
      sel?.focus();
      return;
    }
    const { aiAssessmentOverview } = await import('./services/ai.js');
    runStructured(`Assessment Overview · ${assessmentType}`, btn, aiAssessmentOverview, renderAssessmentOverview, { assessmentType });
  });
  // Reveal the free-text box only when "Other" is picked.
  document.getElementById('aiAssessmentType')?.addEventListener('change', (e) => {
    const other = document.getElementById('aiAssessmentOther');
    if (!other) return;
    other.hidden = e.target.value !== '__other__';
    if (!other.hidden) other.focus();
  });
  // Populate the assessment picker from the catalogue pool + this school's
  // scheduled additions, grouped by domain, with an "Other" free-text escape.
  (async () => {
    try {
      const cat = await import('./data/assessment-catalog.js');
      const DOMAINS = [['lit', 'Literacy'], ['mth', 'Maths'], ['sel', 'Wellbeing / SEL']];
      const seen = new Set();
      const sel = document.getElementById('aiAssessmentType');
      if (!sel) return;
      let html = '<option value="">Choose an assessment…</option>';
      DOMAINS.forEach(([key, label]) => {
        const items = [...new Set(cat.SEC_POOL?.[key] || [])].filter(a => a && !seen.has(a));
        items.forEach(a => seen.add(a));
        if (items.length) html += `<optgroup label="${escA(label)}">${items.sort().map(a => `<option value="${escA(a)}">${escA(a)}</option>`).join('')}</optgroup>`;
      });
      // Any school-scheduled assessments not already in the pool.
      const sched = window.nccd?.assessmentSchedule || {};
      const extra = new Set();
      Object.values(sched).forEach(bySec => Object.values(bySec || {}).forEach(arr => (arr || []).forEach(a => { if (a && !seen.has(a)) extra.add(a); })));
      if (extra.size) html += `<optgroup label="Other (your school)">${[...extra].sort().map(a => `<option value="${escA(a)}">${escA(a)}</option>`).join('')}</optgroup>`;
      html += '<option value="__other__">Other (type your own)…</option>';
      sel.innerHTML = html;
    } catch { /* catalogue optional */ }
  })();

  // Individual NCCD Readiness — runs the per-student scan across the scope,
  // saves results to each profile, then shows a summary list with a clear
  // call-to-action to view the full bars on the Records page (the previous
  // version finished silently, which read as "nothing happened").
  document.getElementById('btnAiReadiness')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    const cohort = getCohort();
    if (!cohort.length) { (await import('./components/toast.js')).toast('No students in scope'); return; }
    const { showConfirm } = await import('./components/confirm-dialog.js');
    showConfirm(
      `Run readiness scan for ${cohort.length} student${cohort.length === 1 ? '' : 's'}?`,
      `Each scan takes around 3 seconds - please leave the page open until it finishes. Results appear on the Records page.`,
      async () => {
        const originalLabel = btn.textContent;
        btn.disabled = true;
        const { aiNccdReadinessScore } = await import('./services/ai.js');
        const { loadProfile, loadNotes, loadMeetings, saveProfile } = await import('./services/students.js');
        const { toast } = await import('./components/toast.js');
        const { buildReadinessBundle } = await import('./utils/ai-bundle.js');
        const scanned = [];
        let done = 0, failed = 0;
        for (const s of cohort) {
          btn.textContent = `⏳ ${done + 1}/${cohort.length}…`;
          try {
            const [profile, notes, meetings] = await Promise.all([loadProfile(s.id), loadNotes(s.id), loadMeetings(s.id)]);
            const enrolment = (window.nccd.enrolments || []).find(en => en.studentId === s.id && en.academicYear === window.nccd.activeYear);
            const bundle = await buildReadinessBundle({
              student: s, profile, notes, meetings,
              sessions: window.nccd.sessions || [],
              interventions: window.nccd.interventions || [],
              library: window.nccd.library || [],
              enrolment,
              activeYear: window.nccd.activeYear,
              config: window.nccd.config || {},
              schoolName: window.nccd.settings?.name || '',
              allStudents: window.nccd.students || [],
            });
            const result = await aiNccdReadinessScore(bundle, { allStudents: window.nccd.students });
            if (result) {
              if (!profile.summary) profile.summary = {};
              profile.summary.readiness = result;
              await saveProfile(s.id, 'summary', profile.summary);
              const records = await import('./pages/records.js');
              records.setReadinessFor?.(s.id, { percent: result.score, complete: 0, total: 0, missing: result.topGaps || [], aiPowered: true, ranAt: result.ranAt, fundingLikelihood: result.fundingLikelihood });
              scanned.push({ name: s.name, score: result.score, likelihood: result.fundingLikelihood });
            }
          } catch (err) { console.error('Scan failed for', s.id, err); failed++; }
          done++;
          await new Promise(r => setTimeout(r, 600));
        }
        btn.disabled = false;
        btn.textContent = originalLabel;
        toast(`Scan complete: ${done - failed} succeeded${failed ? `, ${failed} failed` : ''}`);
        // Results panel + CTA so it's obvious where to look next.
        scanned.sort((a, b) => (a.score || 0) - (b.score || 0));
        const colour = (n) => n >= 80 ? 'good' : n >= 60 ? 'warn' : 'risk';
        const rows = scanned.map(r => `<div class="ai-ready-row ai-ready-${colour(r.score || 0)}"><span class="ai-ready-name">${escA(r.name)}</span><span class="ai-ready-like">${escA(r.likelihood || '')}</span><span class="ai-ready-score">${escA(r.score ?? '-')}%</span></div>`).join('');
        const html = `<div class="ai-ready-cta"><strong>✅ Scanned ${done - failed} student${done - failed === 1 ? '' : 's'}.</strong> Full readiness bars and gaps are now on the Records page. <button class="btn btn-primary btn-sm" id="aiReadinessGoRecords">Go to Records →</button></div>${failed ? `<p class="text-muted">${failed} scan${failed === 1 ? '' : 's'} failed - see console.</p>` : ''}<div class="ai-ready-list">${rows || '<p class="text-muted">No scores returned.</p>'}</div>`;
        showPanel(`Individual NCCD Readiness - ${scopeLabel()}`, html);
        document.getElementById('aiReadinessGoRecords')?.addEventListener('click', () => { window.location.hash = '#/records'; });
      }
    );
  });
}


